import { workflowJsep } from '../../../../lib/formula/jsepInstances';
/**
 * Security Error for workflow expression parser
 */
export class WorkflowSecurityError extends Error {
    constructor(message) {
        super(message);
        this.name = 'WorkflowSecurityError';
    }
}
/**
 * Evaluation Error for workflow expression parser
 */
export class WorkflowEvaluationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'WorkflowEvaluationError';
    }
}
/**
 * Symbol used to identify arrow function wrappers
 */
const ARROW_FN_SYMBOL = Symbol('WorkflowArrowFunction');
/**
 * Whitelisted string methods that are safe to execute
 */
const SAFE_STRING_METHODS = new Set([
    'toUpperCase',
    'toLowerCase',
    'trim',
    'slice',
    'substring',
    'charAt',
    'charCodeAt',
    'indexOf',
    'lastIndexOf',
    'split',
    'replace',
    'startsWith',
    'endsWith',
    'includes',
    'repeat',
    'padStart',
    'padEnd',
    'concat',
    'toString',
    'valueOf',
]);
/**
 * Whitelisted array methods that are safe to execute
 * Note: Mutating methods (push, pop, shift, unshift, reverse, sort) are excluded
 */
const SAFE_ARRAY_METHODS = new Set([
    'join',
    'concat',
    'slice',
    'indexOf',
    'lastIndexOf',
    'includes',
    'find',
    'findIndex',
    'filter',
    'map',
    'reduce',
    'some',
    'every',
]);
/**
 * Whitelisted number methods that are safe to execute
 */
const SAFE_NUMBER_METHODS = new Set([
    'toFixed',
    'toPrecision',
    'toExponential',
    'toString',
]);
/**
 * Whitelisted object methods that are safe to execute
 */
const SAFE_OBJECT_METHODS = new Set(['toString', 'valueOf']);
/**
 * Maximum recursion depth to prevent stack overflow
 */
const MAX_RECURSION_DEPTH = 100;
/**
 * Maximum array/string length to prevent DoS
 */
const MAX_COLLECTION_SIZE = 10000;
/**
 * Maximum number of iterations in loops
 */
const MAX_ITERATIONS = 1000;
/**
 * Precompiled regex for interpolation to prevent ReDoS
 * Uses character class [^{}] to avoid backtracking on nested braces
 */
const INTERPOLATION_REGEX = /\{\{\s*([^{}]+?)\s*}}/g;
/**
 * Secure sandboxed expression parser for automation workflows.
 * Uses AST interpretation (jsep) to safely evaluate expressions without eval.
 */
export class WorkflowExpressionParser {
    constructor() {
        this.context = {};
        this.currentNodeData = null;
        this.inputData = null;
        this.recursionDepth = 0;
        this.iterationCount = 0;
        this.localScope = {};
    }
    /**
     * Set the context data (workflow nodes)
     * @param context - Object mapping node names to their data
     */
    setContext(context) {
        if (typeof context !== 'object' || context === null) {
            throw new WorkflowEvaluationError('Context must be a non-null object');
        }
        // Deep validate context data for security
        this.validateContextData(context);
        this.context = context;
    }
    /**
     * Set the current node data (accessible via $json)
     * @param data - Current node data
     */
    setCurrentNodeData(data) {
        this.currentNodeData = data;
    }
    /**
     * Set the input data (accessible via $input)
     * @param data - Input data
     */
    setInputData(data) {
        this.inputData = data;
    }
    /**
     * Process a string containing {{ }} interpolations
     * @param input - String with expressions to evaluate
     * @returns Processed string with expressions replaced
     */
    processString(input) {
        if (typeof input !== 'string') {
            return String(input);
        }
        // Reset counters for each top-level evaluation
        this.recursionDepth = 0;
        this.iterationCount = 0;
        // Reset regex lastIndex for safety (global regex maintains state)
        INTERPOLATION_REGEX.lastIndex = 0;
        return input.replace(INTERPOLATION_REGEX, (_match, expression) => {
            try {
                const result = this.evaluate(expression);
                return this.stringifyResult(result);
            }
            catch (error) {
                // Return original match if evaluation fails
                console.error('Expression evaluation error:', error);
                throw error;
            }
        });
    }
    /**
     * Evaluate a single expression
     * @param expression - Expression string to evaluate
     * @returns Evaluation result
     */
    evaluate(expression) {
        if (typeof expression !== 'string') {
            throw new WorkflowEvaluationError('Expression must be a string');
        }
        // Reset counters
        this.recursionDepth = 0;
        this.iterationCount = 0;
        try {
            const ast = workflowJsep(expression);
            return this.evaluateNode(ast);
        }
        catch (error) {
            if (error instanceof WorkflowSecurityError ||
                error instanceof WorkflowEvaluationError) {
                throw error;
            }
            throw new WorkflowEvaluationError(`Failed to evaluate expression: ${error.message}`);
        }
    }
    /**
     * Evaluate an AST node
     * @param node - AST node to evaluate
     * @returns Evaluation result
     */
    evaluateNode(node) {
        // Check recursion depth
        this.recursionDepth++;
        if (this.recursionDepth > MAX_RECURSION_DEPTH) {
            throw new WorkflowSecurityError('Maximum recursion depth exceeded');
        }
        try {
            switch (node.type) {
                case 'Literal':
                    return node.value;
                case 'Identifier':
                    return this.evaluateIdentifier(node.name);
                case 'MemberExpression':
                    return this.evaluateMemberExpression(node);
                case 'CallExpression':
                    return this.evaluateCallExpression(node);
                case 'BinaryExpression':
                    return this.evaluateBinaryExpression(node);
                case 'UnaryExpression':
                    return this.evaluateUnaryExpression(node);
                case 'ConditionalExpression':
                    return this.evaluateConditionalExpression(node);
                case 'ArrayExpression':
                    return this.evaluateArrayExpression(node);
                case 'LogicalExpression':
                    return this.evaluateLogicalExpression(node);
                case 'TemplateLiteral':
                    return this.evaluateTemplateLiteral(node);
                case 'ArrowFunctionExpression':
                    return this.createArrowFunction(node);
                default:
                    throw new WorkflowSecurityError(`Unsupported node type: ${node.type}`);
            }
        }
        finally {
            this.recursionDepth--;
        }
    }
    /**
     * Evaluate an identifier (variable name)
     */
    evaluateIdentifier(name) {
        // Check local scope first (for arrow function parameters)
        if (name in this.localScope) {
            return this.localScope[name];
        }
        // Check for dangerous identifiers
        this.validateIdentifier(name);
        // Handle special identifiers
        if (name === '$json') {
            return this.currentNodeData;
        }
        if (name === '$input') {
            return this.inputData;
        }
        // Return undefined for unknown identifiers
        return undefined;
    }
    /**
     * Evaluate a member expression (e.g., obj.prop or obj[prop])
     */
    evaluateMemberExpression(node) {
        const object = this.evaluateNode(node.object);
        if (object === null || object === undefined) {
            return undefined;
        }
        let property;
        if (node.computed) {
            // Bracket notation: obj[expr]
            property = this.evaluateNode(node.property);
        }
        else {
            // Dot notation: obj.prop
            property = node.property.name;
        }
        // Validate property access
        this.validatePropertyAccess(property);
        // Safe property access
        return this.safePropertyAccess(object, property);
    }
    /**
     * Evaluate a call expression (function call)
     */
    evaluateCallExpression(node) {
        // Evaluate arguments first
        const args = node.arguments.map((arg) => this.evaluateNode(arg));
        // Check if it's a built-in function
        if (node.callee.type === 'Identifier') {
            const fnName = node.callee.name;
            // Handle special functions
            if (fnName === '$') {
                return this.handleNodeAccess(args);
            }
            // Handle built-in functions
            const builtInFn = this.getBuiltInFunction(fnName);
            if (builtInFn) {
                return builtInFn(...args);
            }
            throw new WorkflowSecurityError(`Unknown function: ${fnName}`);
        }
        // Handle method calls (e.g., str.toUpperCase())
        if (node.callee.type === 'MemberExpression') {
            const object = this.evaluateNode(node.callee.object);
            const methodName = node.callee.property.name;
            return this.callMethod(object, methodName, args);
        }
        throw new WorkflowSecurityError('Invalid function call');
    }
    /**
     * Evaluate a binary expression (e.g., a + b, a > b)
     */
    evaluateBinaryExpression(node) {
        // Handle logical operators with short-circuit evaluation
        if (node.operator === '&&' || node.operator === '||') {
            const left = !!this.evaluateNode(node.left);
            if (node.operator === '&&') {
                return left && !!this.evaluateNode(node.right);
            }
            else {
                return left || !!this.evaluateNode(node.right);
            }
        }
        const left = this.evaluateNode(node.left);
        const right = this.evaluateNode(node.right);
        switch (node.operator) {
            // Arithmetic operators
            case '+':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Arithmetic addition requires both operands to be numbers. Use concat() for strings.');
                }
                return left + right;
            case '-':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Subtraction requires both operands to be numbers');
                }
                return left - right;
            case '*':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Multiplication requires both operands to be numbers');
                }
                return left * right;
            case '/': {
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Division requires both operands to be numbers');
                }
                // Check for zero (including -0)
                if (right === 0 || Object.is(right, -0)) {
                    throw new WorkflowEvaluationError('Division by zero');
                }
                const result = left / right;
                // Check for Infinity (overflow)
                if (!isFinite(result)) {
                    throw new WorkflowEvaluationError('Division result is too large (Infinity)');
                }
                return result;
            }
            case '%':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Modulo requires both operands to be numbers');
                }
                if (right === 0 || Object.is(right, -0)) {
                    throw new WorkflowEvaluationError('Modulo by zero');
                }
                // Note: JavaScript % can return negative results (e.g., -5 % 3 = -2)
                // This is remainder, not mathematical modulo
                return left % right;
            // Comparison operators
            case '==':
            case '===':
                return left === right;
            case '!=':
            case '!==':
                return left !== right;
            case '<':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Comparison operators require both operands to be numbers');
                }
                return left < right;
            case '>':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Comparison operators require both operands to be numbers');
                }
                return left > right;
            case '<=':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Comparison operators require both operands to be numbers');
                }
                return left <= right;
            case '>=':
                if (typeof left !== 'number' || typeof right !== 'number') {
                    throw new WorkflowEvaluationError('Comparison operators require both operands to be numbers');
                }
                return left >= right;
            default:
                throw new WorkflowSecurityError(`Unsupported operator: ${node.operator}`);
        }
    }
    /**
     * Evaluate a unary expression (e.g., -x, !x)
     */
    evaluateUnaryExpression(node) {
        const argument = this.evaluateNode(node.argument);
        switch (node.operator) {
            case '-':
                if (typeof argument !== 'number' || isNaN(argument)) {
                    throw new WorkflowSecurityError(`Unary '-' operator requires a valid number, got ${typeof argument}`);
                }
                return -argument;
            case '+':
                if (typeof argument !== 'number' || isNaN(argument)) {
                    throw new WorkflowSecurityError(`Unary '+' operator requires a valid number, got ${typeof argument}`);
                }
                return +argument;
            case '!':
                return !argument;
            default:
                throw new WorkflowSecurityError(`Unsupported unary operator: ${node.operator}`);
        }
    }
    /**
     * Evaluate a conditional expression (ternary operator)
     */
    evaluateConditionalExpression(node) {
        const test = this.evaluateNode(node.test);
        return test
            ? this.evaluateNode(node.consequent)
            : this.evaluateNode(node.alternate);
    }
    /**
     * Evaluate an array expression
     */
    evaluateArrayExpression(node) {
        const elements = node.elements.map((element) => this.evaluateNode(element));
        // Check array size
        if (elements.length > MAX_COLLECTION_SIZE) {
            throw new WorkflowSecurityError('Array size exceeds maximum allowed');
        }
        return elements;
    }
    /**
     * Evaluate a logical expression (&&, ||)
     */
    evaluateLogicalExpression(node) {
        const left = this.evaluateNode(node.left);
        switch (node.operator) {
            case '&&':
                return left ? this.evaluateNode(node.right) : left;
            case '||':
                return left ? left : this.evaluateNode(node.right);
            default:
                throw new WorkflowSecurityError(`Unsupported logical operator: ${node.operator}`);
        }
    }
    /**
     * Evaluate a template literal
     */
    evaluateTemplateLiteral(node) {
        let result = '';
        for (let i = 0; i < node.quasis.length; i++) {
            result += node.quasis[i].value.cooked;
            if (i < node.expressions.length) {
                result += this.stringifyResult(this.evaluateNode(node.expressions[i]));
            }
        }
        return result;
    }
    /**
     * Create an arrow function wrapper (AST representation, not actual JS function)
     * This wrapper will be recognized and evaluated by array methods
     */
    createArrowFunction(node) {
        const params = node.params.map((p) => {
            this.validateIdentifier(p.name);
            if (p.name.startsWith('$')) {
                throw new WorkflowSecurityError(`Parameter name "${p.name}" cannot start with $`);
            }
            return p.name;
        });
        const body = node.body;
        return {
            [ARROW_FN_SYMBOL]: true,
            params,
            body,
        };
    }
    /**
     * Check if a value is an arrow function wrapper
     */
    isArrowFunction(value) {
        return (typeof value === 'object' && value !== null && ARROW_FN_SYMBOL in value);
    }
    /**
     * Evaluate an arrow function with given arguments
     */
    evaluateArrowFunction(arrowFn, args) {
        // Save previous local scope
        const previousScope = Object.assign({}, this.localScope);
        try {
            // Set arrow function parameters in local scope
            arrowFn.params.forEach((param, index) => {
                this.localScope[param] = args[index];
            });
            // Evaluate the body of the arrow function
            return this.evaluateNode(arrowFn.body);
        }
        finally {
            // Restore previous local scope
            this.localScope = previousScope;
        }
    }
    /**
     * Handle $() node access function
     */
    handleNodeAccess(args) {
        if (args.length !== 1 || typeof args[0] !== 'string') {
            throw new WorkflowEvaluationError('$() requires exactly one string argument');
        }
        const nodeName = args[0];
        // Validate node name
        this.validateIdentifier(nodeName);
        if (!(nodeName in this.context)) {
            throw new WorkflowEvaluationError(`Node "${nodeName}" not found in context`);
        }
        return this.context[nodeName];
    }
    /**
     * Get built-in function by name
     */
    getBuiltInFunction(name) {
        const builtInFns = {
            // Special variables
            $json: () => this.currentNodeData,
            $input: () => this.inputData,
            // Utility functions
            $ifEmpty: (value, defaultValue) => {
                return value === null || value === undefined || value === ''
                    ? defaultValue
                    : value;
            },
            // Type checking
            $isNull: (value) => value === null,
            $isUndefined: (value) => value === undefined,
            $isEmpty: (value) => {
                if (value === null || value === undefined)
                    return true;
                if (typeof value === 'string')
                    return value.length === 0;
                if (Array.isArray(value))
                    return value.length === 0;
                if (typeof value === 'object') {
                    try {
                        return Object.keys(value).length === 0;
                    }
                    catch (_a) {
                        // If Object.keys() throws (e.g., special objects), consider it not empty
                        return false;
                    }
                }
                return false;
            },
            // Type conversion
            $string: (value) => String(value),
            $number: (value) => {
                const num = Number(value);
                if (isNaN(num)) {
                    throw new WorkflowEvaluationError('Cannot convert to number');
                }
                return num;
            },
            $boolean: (value) => Boolean(value),
            // Array functions
            $length: (value) => {
                if (typeof value === 'string' || Array.isArray(value)) {
                    return value.length;
                }
                throw new WorkflowEvaluationError('$length requires string or array');
            },
            $first: (arr) => {
                if (!Array.isArray(arr)) {
                    throw new WorkflowEvaluationError('$first requires an array');
                }
                return arr[0];
            },
            $last: (arr) => {
                if (!Array.isArray(arr)) {
                    throw new WorkflowEvaluationError('$last requires an array');
                }
                return arr[arr.length - 1];
            },
            // Math functions
            $abs: (value) => {
                if (typeof value !== 'number' || isNaN(value)) {
                    throw new WorkflowEvaluationError('$abs requires a valid number');
                }
                return Math.abs(value);
            },
            $ceil: (value) => {
                if (typeof value !== 'number' || isNaN(value)) {
                    throw new WorkflowEvaluationError('$ceil requires a valid number');
                }
                return Math.ceil(value);
            },
            $floor: (value) => {
                if (typeof value !== 'number' || isNaN(value)) {
                    throw new WorkflowEvaluationError('$floor requires a valid number');
                }
                return Math.floor(value);
            },
            $round: (value) => {
                if (typeof value !== 'number' || isNaN(value)) {
                    throw new WorkflowEvaluationError('$round requires a valid number');
                }
                return Math.round(value);
            },
            $min: (...values) => {
                if (values.length === 0) {
                    throw new WorkflowEvaluationError('$min requires at least one argument');
                }
                const numbers = values.map((v, i) => {
                    if (typeof v !== 'number' || isNaN(v)) {
                        throw new WorkflowEvaluationError(`$min argument ${i + 1} must be a valid number`);
                    }
                    return v;
                });
                return Math.min(...numbers);
            },
            $max: (...values) => {
                if (values.length === 0) {
                    throw new WorkflowEvaluationError('$max requires at least one argument');
                }
                const numbers = values.map((v, i) => {
                    if (typeof v !== 'number' || isNaN(v)) {
                        throw new WorkflowEvaluationError(`$max argument ${i + 1} must be a valid number`);
                    }
                    return v;
                });
                return Math.max(...numbers);
            },
        };
        return builtInFns[name] || null;
    }
    /**
     * Call a method on an object
     */
    callMethod(object, methodName, args) {
        if (object === null || object === undefined) {
            throw new WorkflowEvaluationError('Cannot call method on null or undefined');
        }
        // Validate method name
        this.validateMethodName(methodName);
        // Check if method is whitelisted for this type
        if (!this.isMethodAllowed(object, methodName)) {
            throw new WorkflowSecurityError(`Method "${methodName}" is not allowed`);
        }
        // Handle array methods with arrow function callbacks manually
        if (Array.isArray(object) && this.isCallbackMethod(methodName)) {
            const callback = args[0];
            if (this.isArrowFunction(callback)) {
                return this.evaluateArrayMethodWithArrowFunction(object, methodName, callback, args.slice(1));
            }
        }
        // Validate callback arguments for array methods that accept them
        if (this.isCallbackMethod(methodName)) {
            this.validateCallbackArguments(args);
        }
        // Check iteration limit for iterative methods
        if (this.isIterativeMethod(methodName)) {
            this.iterationCount++;
            if (this.iterationCount > MAX_ITERATIONS) {
                throw new WorkflowSecurityError('Maximum iteration count exceeded');
            }
        }
        // Get the method
        const method = object[methodName];
        if (typeof method !== 'function') {
            throw new WorkflowEvaluationError(`"${methodName}" is not a function`);
        }
        // Call the method safely
        try {
            const result = method.apply(object, args);
            // Validate result size
            if (Array.isArray(result) && result.length > MAX_COLLECTION_SIZE) {
                throw new WorkflowSecurityError('Result array size exceeds maximum allowed');
            }
            if (typeof result === 'string' && result.length > MAX_COLLECTION_SIZE) {
                throw new WorkflowSecurityError('Result string length exceeds maximum allowed');
            }
            return result;
        }
        catch (error) {
            if (error instanceof WorkflowSecurityError) {
                throw error;
            }
            throw new WorkflowEvaluationError(`Method "${methodName}" failed: ${error.message}`);
        }
    }
    /**
     * Manually evaluate array methods with arrow functions
     * This avoids creating actual JS functions and keeps everything in AST evaluation
     */
    evaluateArrayMethodWithArrowFunction(array, methodName, arrowFn, extraArgs) {
        // Check iteration limit
        this.iterationCount++;
        if (this.iterationCount > MAX_ITERATIONS) {
            throw new WorkflowSecurityError('Maximum iteration count exceeded');
        }
        // Validate array size
        if (array.length > MAX_COLLECTION_SIZE) {
            throw new WorkflowSecurityError('Array size exceeds maximum allowed');
        }
        switch (methodName) {
            case 'map': {
                const result = [];
                for (let i = 0; i < array.length; i++) {
                    const value = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    result.push(value);
                }
                return result;
            }
            case 'filter': {
                const result = [];
                for (let i = 0; i < array.length; i++) {
                    const shouldInclude = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    if (shouldInclude) {
                        result.push(array[i]);
                    }
                }
                return result;
            }
            case 'find': {
                for (let i = 0; i < array.length; i++) {
                    const matches = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    if (matches) {
                        return array[i];
                    }
                }
                return undefined;
            }
            case 'findIndex': {
                for (let i = 0; i < array.length; i++) {
                    const matches = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    if (matches) {
                        return i;
                    }
                }
                return -1;
            }
            case 'findLastIndex': {
                for (let i = array.length - 1; i >= 0; i--) {
                    const matches = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    if (matches) {
                        return i;
                    }
                }
                return -1;
            }
            case 'some': {
                for (let i = 0; i < array.length; i++) {
                    const matches = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    if (matches) {
                        return true;
                    }
                }
                return false;
            }
            case 'every': {
                for (let i = 0; i < array.length; i++) {
                    const matches = this.evaluateArrowFunction(arrowFn, [
                        array[i],
                        i,
                        array,
                    ]);
                    if (!matches) {
                        return false;
                    }
                }
                return true;
            }
            case 'reduce': {
                if (array.length === 0 && extraArgs.length === 0) {
                    throw new WorkflowEvaluationError('Reduce of empty array with no initial value');
                }
                let accumulator;
                let startIndex;
                if (extraArgs.length > 0) {
                    accumulator = extraArgs[0];
                    startIndex = 0;
                }
                else {
                    accumulator = array[0];
                    startIndex = 1;
                }
                for (let i = startIndex; i < array.length; i++) {
                    accumulator = this.evaluateArrowFunction(arrowFn, [
                        accumulator,
                        array[i],
                        i,
                        array,
                    ]);
                }
                return accumulator;
            }
            default:
                throw new WorkflowSecurityError(`Array method "${methodName}" with arrow functions is not supported`);
        }
    }
    /**
     * Check if a method is allowed for the given object type
     */
    isMethodAllowed(object, methodName) {
        if (typeof object === 'string') {
            return SAFE_STRING_METHODS.has(methodName);
        }
        if (Array.isArray(object)) {
            return SAFE_ARRAY_METHODS.has(methodName);
        }
        if (typeof object === 'number') {
            return SAFE_NUMBER_METHODS.has(methodName);
        }
        if (typeof object === 'object' && object !== null) {
            return SAFE_OBJECT_METHODS.has(methodName);
        }
        return false;
    }
    /**
     * Check if a method is iterative (for iteration counting)
     */
    isIterativeMethod(methodName) {
        return [
            'map',
            'filter',
            'reduce',
            'find',
            'findIndex',
            'findLastIndex',
            'some',
            'every',
        ].includes(methodName);
    }
    /**
     * Check if a method accepts callback functions
     */
    isCallbackMethod(methodName) {
        return [
            'map',
            'filter',
            'reduce',
            'find',
            'findIndex',
            'some',
            'every',
            'forEach',
        ].includes(methodName);
    }
    /**
     * Validate callback arguments to prevent function injection
     * Allows arrow function wrappers created by the parser, but blocks external functions
     */
    validateCallbackArguments(args) {
        for (const arg of args) {
            // Allow arrow function wrappers created by the parser
            if (this.isArrowFunction(arg)) {
                continue;
            }
            // Block any actual JavaScript functions (security risk)
            if (typeof arg === 'function') {
                throw new WorkflowSecurityError('Callback functions are not supported. Use arrow function syntax in expressions instead.');
            }
            // Check for objects that might contain external functions
            if (arg !== null && typeof arg === 'object') {
                this.validateContextData(arg, 0, new WeakSet());
            }
        }
    }
    /**
     * Safely access a property on an object
     */
    safePropertyAccess(object, property) {
        if (object === null || object === undefined) {
            return undefined;
        }
        // Prevent prototype pollution
        if (property === '__proto__' ||
            property === 'constructor' ||
            property === 'prototype') {
            throw new WorkflowSecurityError('Access to prototype properties is not allowed');
        }
        return object[property];
    }
    /**
     * Validate an identifier name
     */
    validateIdentifier(name, isRoot = true) {
        // Block dangerous identifiers for root access
        if (isRoot) {
            const rootDangerous = [
                'eval',
                'Function',
                'constructor',
                '__proto__',
                'prototype',
                'process',
                'require',
                'import',
                'global',
                'globalThis',
                'window',
                'document',
                'location',
                'localStorage',
                'sessionStorage',
            ];
            if (rootDangerous.includes(name)) {
                throw new WorkflowSecurityError(`Root identifier "${name}" is not allowed`);
            }
        }
        else {
            // Less restrictive validation for non-root property access
            const nonRootDangerous = [
                '__proto__',
                'constructor',
                'prototype',
            ];
            if (nonRootDangerous.includes(name)) {
                throw new WorkflowSecurityError(`Property "${name}" is not allowed`);
            }
        }
    }
    /**
     * Validate a property name
     */
    validatePropertyAccess(property) {
        if (typeof property === 'string') {
            this.validateIdentifier(property, false);
        }
    }
    /**
     * Validate a method name
     */
    validateMethodName(name) {
        this.validateIdentifier(name, false);
    }
    /**
     * Deep validate context data for security
     * Checks for functions, circular references, and excessive nesting
     */
    validateContextData(data, depth = 0, seen = new WeakSet()) {
        // Check nesting depth to prevent stack overflow
        if (depth > 10) {
            throw new WorkflowSecurityError('Context nesting depth exceeds maximum allowed (10 levels)');
        }
        // Reject functions
        if (typeof data === 'function') {
            throw new WorkflowSecurityError('Functions are not allowed in context data');
        }
        // Handle arrays
        if (Array.isArray(data)) {
            // Check for circular references
            if (seen.has(data)) {
                throw new WorkflowSecurityError('Circular references are not allowed in context data');
            }
            seen.add(data);
            // Validate each element
            for (const item of data) {
                this.validateContextData(item, depth + 1, seen);
            }
            return;
        }
        // Handle objects
        if (data !== null && typeof data === 'object') {
            // Check for circular references
            if (seen.has(data)) {
                throw new WorkflowSecurityError('Circular references are not allowed in context data');
            }
            seen.add(data);
            // Validate each property
            for (const key in data) {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    // Validate property name
                    this.validateIdentifier(key, false);
                    // Validate property value
                    this.validateContextData(data[key], depth + 1, seen);
                }
            }
        }
        // Primitives (string, number, boolean, null, undefined) are safe
    }
    /**
     * Convert a result to a string for interpolation
     */
    stringifyResult(value) {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        if (Array.isArray(value) || typeof value === 'object') {
            try {
                return JSON.stringify(value);
            }
            catch (_a) {
                return '[Object]';
            }
        }
        return String(value);
    }
}
export default WorkflowExpressionParser;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaW5kZXguanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi8uLi8uLi9zcmMvZWUvbGliL3dvcmtmbG93L3BhcnNlci9pbmRleC50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQSxPQUFPLEVBQUUsWUFBWSxFQUFFLE1BQU0sNkJBQTZCLENBQUM7QUFFM0Q7O0dBRUc7QUFDSCxNQUFNLE9BQU8scUJBQXNCLFNBQVEsS0FBSztJQUM5QyxZQUFZLE9BQWU7UUFDekIsS0FBSyxDQUFDLE9BQU8sQ0FBQyxDQUFDO1FBQ2YsSUFBSSxDQUFDLElBQUksR0FBRyx1QkFBdUIsQ0FBQztJQUN0QyxDQUFDO0NBQ0Y7QUFFRDs7R0FFRztBQUNILE1BQU0sT0FBTyx1QkFBd0IsU0FBUSxLQUFLO0lBQ2hELFlBQVksT0FBZTtRQUN6QixLQUFLLENBQUMsT0FBTyxDQUFDLENBQUM7UUFDZixJQUFJLENBQUMsSUFBSSxHQUFHLHlCQUF5QixDQUFDO0lBQ3hDLENBQUM7Q0FDRjtBQUVEOztHQUVHO0FBQ0gsTUFBTSxlQUFlLEdBQUcsTUFBTSxDQUFDLHVCQUF1QixDQUFDLENBQUM7QUFZeEQ7O0dBRUc7QUFDSCxNQUFNLG1CQUFtQixHQUFHLElBQUksR0FBRyxDQUFDO0lBQ2xDLGFBQWE7SUFDYixhQUFhO0lBQ2IsTUFBTTtJQUNOLE9BQU87SUFDUCxXQUFXO0lBQ1gsUUFBUTtJQUNSLFlBQVk7SUFDWixTQUFTO0lBQ1QsYUFBYTtJQUNiLE9BQU87SUFDUCxTQUFTO0lBQ1QsWUFBWTtJQUNaLFVBQVU7SUFDVixVQUFVO0lBQ1YsUUFBUTtJQUNSLFVBQVU7SUFDVixRQUFRO0lBQ1IsUUFBUTtJQUNSLFVBQVU7SUFDVixTQUFTO0NBQ1YsQ0FBQyxDQUFDO0FBRUg7OztHQUdHO0FBQ0gsTUFBTSxrQkFBa0IsR0FBRyxJQUFJLEdBQUcsQ0FBQztJQUNqQyxNQUFNO0lBQ04sUUFBUTtJQUNSLE9BQU87SUFDUCxTQUFTO0lBQ1QsYUFBYTtJQUNiLFVBQVU7SUFDVixNQUFNO0lBQ04sV0FBVztJQUNYLFFBQVE7SUFDUixLQUFLO0lBQ0wsUUFBUTtJQUNSLE1BQU07SUFDTixPQUFPO0NBQ1IsQ0FBQyxDQUFDO0FBRUg7O0dBRUc7QUFDSCxNQUFNLG1CQUFtQixHQUFHLElBQUksR0FBRyxDQUFDO0lBQ2xDLFNBQVM7SUFDVCxhQUFhO0lBQ2IsZUFBZTtJQUNmLFVBQVU7Q0FDWCxDQUFDLENBQUM7QUFFSDs7R0FFRztBQUNILE1BQU0sbUJBQW1CLEdBQUcsSUFBSSxHQUFHLENBQUMsQ0FBQyxVQUFVLEVBQUUsU0FBUyxDQUFDLENBQUMsQ0FBQztBQUU3RDs7R0FFRztBQUNILE1BQU0sbUJBQW1CLEdBQUcsR0FBRyxDQUFDO0FBRWhDOztHQUVHO0FBQ0gsTUFBTSxtQkFBbUIsR0FBRyxLQUFLLENBQUM7QUFFbEM7O0dBRUc7QUFDSCxNQUFNLGNBQWMsR0FBRyxJQUFJLENBQUM7QUFFNUI7OztHQUdHO0FBQ0gsTUFBTSxtQkFBbUIsR0FBRyx3QkFBd0IsQ0FBQztBQUVyRDs7O0dBR0c7QUFDSCxNQUFNLE9BQU8sd0JBQXdCO0lBUW5DO1FBQ0UsSUFBSSxDQUFDLE9BQU8sR0FBRyxFQUFFLENBQUM7UUFDbEIsSUFBSSxDQUFDLGVBQWUsR0FBRyxJQUFJLENBQUM7UUFDNUIsSUFBSSxDQUFDLFNBQVMsR0FBRyxJQUFJLENBQUM7UUFDdEIsSUFBSSxDQUFDLGNBQWMsR0FBRyxDQUFDLENBQUM7UUFDeEIsSUFBSSxDQUFDLGNBQWMsR0FBRyxDQUFDLENBQUM7UUFDeEIsSUFBSSxDQUFDLFVBQVUsR0FBRyxFQUFFLENBQUM7SUFDdkIsQ0FBQztJQUVEOzs7T0FHRztJQUNJLFVBQVUsQ0FBQyxPQUFnQztRQUNoRCxJQUFJLE9BQU8sT0FBTyxLQUFLLFFBQVEsSUFBSSxPQUFPLEtBQUssSUFBSSxFQUFFLENBQUM7WUFDcEQsTUFBTSxJQUFJLHVCQUF1QixDQUFDLG1DQUFtQyxDQUFDLENBQUM7UUFDekUsQ0FBQztRQUNELDBDQUEwQztRQUMxQyxJQUFJLENBQUMsbUJBQW1CLENBQUMsT0FBTyxDQUFDLENBQUM7UUFDbEMsSUFBSSxDQUFDLE9BQU8sR0FBRyxPQUFPLENBQUM7SUFDekIsQ0FBQztJQUVEOzs7T0FHRztJQUNJLGtCQUFrQixDQUFDLElBQWE7UUFDckMsSUFBSSxDQUFDLGVBQWUsR0FBRyxJQUFJLENBQUM7SUFDOUIsQ0FBQztJQUVEOzs7T0FHRztJQUNJLFlBQVksQ0FBQyxJQUFhO1FBQy9CLElBQUksQ0FBQyxTQUFTLEdBQUcsSUFBSSxDQUFDO0lBQ3hCLENBQUM7SUFFRDs7OztPQUlHO0lBQ0ksYUFBYSxDQUFDLEtBQWE7UUFDaEMsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLEVBQUUsQ0FBQztZQUM5QixPQUFPLE1BQU0sQ0FBQyxLQUFLLENBQUMsQ0FBQztRQUN2QixDQUFDO1FBRUQsK0NBQStDO1FBQy9DLElBQUksQ0FBQyxjQUFjLEdBQUcsQ0FBQyxDQUFDO1FBQ3hCLElBQUksQ0FBQyxjQUFjLEdBQUcsQ0FBQyxDQUFDO1FBRXhCLGtFQUFrRTtRQUNsRSxtQkFBbUIsQ0FBQyxTQUFTLEdBQUcsQ0FBQyxDQUFDO1FBRWxDLE9BQU8sS0FBSyxDQUFDLE9BQU8sQ0FBQyxtQkFBbUIsRUFBRSxDQUFDLE1BQU0sRUFBRSxVQUFVLEVBQUUsRUFBRTtZQUMvRCxJQUFJLENBQUM7Z0JBQ0gsTUFBTSxNQUFNLEdBQUcsSUFBSSxDQUFDLFFBQVEsQ0FBQyxVQUFVLENBQUMsQ0FBQztnQkFDekMsT0FBTyxJQUFJLENBQUMsZUFBZSxDQUFDLE1BQU0sQ0FBQyxDQUFDO1lBQ3RDLENBQUM7WUFBQyxPQUFPLEtBQUssRUFBRSxDQUFDO2dCQUNmLDRDQUE0QztnQkFDNUMsT0FBTyxDQUFDLEtBQUssQ0FBQyw4QkFBOEIsRUFBRSxLQUFLLENBQUMsQ0FBQztnQkFDckQsTUFBTSxLQUFLLENBQUM7WUFDZCxDQUFDO1FBQ0gsQ0FBQyxDQUFDLENBQUM7SUFDTCxDQUFDO0lBRUQ7Ozs7T0FJRztJQUNJLFFBQVEsQ0FBQyxVQUFrQjtRQUNoQyxJQUFJLE9BQU8sVUFBVSxLQUFLLFFBQVEsRUFBRSxDQUFDO1lBQ25DLE1BQU0sSUFBSSx1QkFBdUIsQ0FBQyw2QkFBNkIsQ0FBQyxDQUFDO1FBQ25FLENBQUM7UUFFRCxpQkFBaUI7UUFDakIsSUFBSSxDQUFDLGNBQWMsR0FBRyxDQUFDLENBQUM7UUFDeEIsSUFBSSxDQUFDLGNBQWMsR0FBRyxDQUFDLENBQUM7UUFFeEIsSUFBSSxDQUFDO1lBQ0gsTUFBTSxHQUFHLEdBQUcsWUFBWSxDQUFDLFVBQVUsQ0FBQyxDQUFDO1lBQ3JDLE9BQU8sSUFBSSxDQUFDLFlBQVksQ0FBQyxHQUFHLENBQUMsQ0FBQztRQUNoQyxDQUFDO1FBQUMsT0FBTyxLQUFLLEVBQUUsQ0FBQztZQUNmLElBQ0UsS0FBSyxZQUFZLHFCQUFxQjtnQkFDdEMsS0FBSyxZQUFZLHVCQUF1QixFQUN4QyxDQUFDO2dCQUNELE1BQU0sS0FBSyxDQUFDO1lBQ2QsQ0FBQztZQUNELE1BQU0sSUFBSSx1QkFBdUIsQ0FDL0Isa0NBQWtDLEtBQUssQ0FBQyxPQUFPLEVBQUUsQ0FDbEQsQ0FBQztRQUNKLENBQUM7SUFDSCxDQUFDO0lBRUQ7Ozs7T0FJRztJQUNLLFlBQVksQ0FBQyxJQUFTO1FBQzVCLHdCQUF3QjtRQUN4QixJQUFJLENBQUMsY0FBYyxFQUFFLENBQUM7UUFDdEIsSUFBSSxJQUFJLENBQUMsY0FBYyxHQUFHLG1CQUFtQixFQUFFLENBQUM7WUFDOUMsTUFBTSxJQUFJLHFCQUFxQixDQUFDLGtDQUFrQyxDQUFDLENBQUM7UUFDdEUsQ0FBQztRQUVELElBQUksQ0FBQztZQUNILFFBQVEsSUFBSSxDQUFDLElBQUksRUFBRSxDQUFDO2dCQUNsQixLQUFLLFNBQVM7b0JBQ1osT0FBTyxJQUFJLENBQUMsS0FBSyxDQUFDO2dCQUVwQixLQUFLLFlBQVk7b0JBQ2YsT0FBTyxJQUFJLENBQUMsa0JBQWtCLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxDQUFDO2dCQUU1QyxLQUFLLGtCQUFrQjtvQkFDckIsT0FBTyxJQUFJLENBQUMsd0JBQXdCLENBQUMsSUFBSSxDQUFDLENBQUM7Z0JBRTdDLEtBQUssZ0JBQWdCO29CQUNuQixPQUFPLElBQUksQ0FBQyxzQkFBc0IsQ0FBQyxJQUFJLENBQUMsQ0FBQztnQkFFM0MsS0FBSyxrQkFBa0I7b0JBQ3JCLE9BQU8sSUFBSSxDQUFDLHdCQUF3QixDQUFDLElBQUksQ0FBQyxDQUFDO2dCQUU3QyxLQUFLLGlCQUFpQjtvQkFDcEIsT0FBTyxJQUFJLENBQUMsdUJBQXVCLENBQUMsSUFBSSxDQUFDLENBQUM7Z0JBRTVDLEtBQUssdUJBQXVCO29CQUMxQixPQUFPLElBQUksQ0FBQyw2QkFBNkIsQ0FBQyxJQUFJLENBQUMsQ0FBQztnQkFFbEQsS0FBSyxpQkFBaUI7b0JBQ3BCLE9BQU8sSUFBSSxDQUFDLHVCQUF1QixDQUFDLElBQUksQ0FBQyxDQUFDO2dCQUU1QyxLQUFLLG1CQUFtQjtvQkFDdEIsT0FBTyxJQUFJLENBQUMseUJBQXlCLENBQUMsSUFBSSxDQUFDLENBQUM7Z0JBRTlDLEtBQUssaUJBQWlCO29CQUNwQixPQUFPLElBQUksQ0FBQyx1QkFBdUIsQ0FBQyxJQUFJLENBQUMsQ0FBQztnQkFFNUMsS0FBSyx5QkFBeUI7b0JBQzVCLE9BQU8sSUFBSSxDQUFDLG1CQUFtQixDQUFDLElBQUksQ0FBQyxDQUFDO2dCQUV4QztvQkFDRSxNQUFNLElBQUkscUJBQXFCLENBQzdCLDBCQUEwQixJQUFJLENBQUMsSUFBSSxFQUFFLENBQ3RDLENBQUM7WUFDTixDQUFDO1FBQ0gsQ0FBQztnQkFBUyxDQUFDO1lBQ1QsSUFBSSxDQUFDLGNBQWMsRUFBRSxDQUFDO1FBQ3hCLENBQUM7SUFDSCxDQUFDO0lBRUQ7O09BRUc7SUFDSyxrQkFBa0IsQ0FBQyxJQUFZO1FBQ3JDLDBEQUEwRDtRQUMxRCxJQUFJLElBQUksSUFBSSxJQUFJLENBQUMsVUFBVSxFQUFFLENBQUM7WUFDNUIsT0FBTyxJQUFJLENBQUMsVUFBVSxDQUFDLElBQUksQ0FBQyxDQUFDO1FBQy9CLENBQUM7UUFFRCxrQ0FBa0M7UUFDbEMsSUFBSSxDQUFDLGtCQUFrQixDQUFDLElBQUksQ0FBQyxDQUFDO1FBRTlCLDZCQUE2QjtRQUM3QixJQUFJLElBQUksS0FBSyxPQUFPLEVBQUUsQ0FBQztZQUNyQixPQUFPLElBQUksQ0FBQyxlQUFlLENBQUM7UUFDOUIsQ0FBQztRQUNELElBQUksSUFBSSxLQUFLLFFBQVEsRUFBRSxDQUFDO1lBQ3RCLE9BQU8sSUFBSSxDQUFDLFNBQVMsQ0FBQztRQUN4QixDQUFDO1FBRUQsMkNBQTJDO1FBQzNDLE9BQU8sU0FBUyxDQUFDO0lBQ25CLENBQUM7SUFFRDs7T0FFRztJQUNLLHdCQUF3QixDQUFDLElBQVM7UUFDeEMsTUFBTSxNQUFNLEdBQUcsSUFBSSxDQUFDLFlBQVksQ0FBQyxJQUFJLENBQUMsTUFBTSxDQUFDLENBQUM7UUFFOUMsSUFBSSxNQUFNLEtBQUssSUFBSSxJQUFJLE1BQU0sS0FBSyxTQUFTLEVBQUUsQ0FBQztZQUM1QyxPQUFPLFNBQVMsQ0FBQztRQUNuQixDQUFDO1FBRUQsSUFBSSxRQUF5QixDQUFDO1FBQzlCLElBQUksSUFBSSxDQUFDLFFBQVEsRUFBRSxDQUFDO1lBQ2xCLDhCQUE4QjtZQUM5QixRQUFRLEdBQUcsSUFBSSxDQUFDLFlBQVksQ0FBQyxJQUFJLENBQUMsUUFBUSxDQUFvQixDQUFDO1FBQ2pFLENBQUM7YUFBTSxDQUFDO1lBQ04seUJBQXlCO1lBQ3pCLFFBQVEsR0FBRyxJQUFJLENBQUMsUUFBUSxDQUFDLElBQUksQ0FBQztRQUNoQyxDQUFDO1FBRUQsMkJBQTJCO1FBQzNCLElBQUksQ0FBQyxzQkFBc0IsQ0FBQyxRQUFRLENBQUMsQ0FBQztRQUV0Qyx1QkFBdUI7UUFDdkIsT0FBTyxJQUFJLENBQUMsa0JBQWtCLENBQUMsTUFBTSxFQUFFLFFBQVEsQ0FBQyxDQUFDO0lBQ25ELENBQUM7SUFFRDs7T0FFRztJQUNLLHNCQUFzQixDQUFDLElBQVM7UUFDdEMsMkJBQTJCO1FBQzNCLE1BQU0sSUFBSSxHQUFHLElBQUksQ0FBQyxTQUFTLENBQUMsR0FBRyxDQUFDLENBQUMsR0FBUSxFQUFFLEVBQUUsQ0FBQyxJQUFJLENBQUMsWUFBWSxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUM7UUFFdEUsb0NBQW9DO1FBQ3BDLElBQUksSUFBSSxDQUFDLE1BQU0sQ0FBQyxJQUFJLEtBQUssWUFBWSxFQUFFLENBQUM7WUFDdEMsTUFBTSxNQUFNLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxJQUFJLENBQUM7WUFFaEMsMkJBQTJCO1lBQzNCLElBQUksTUFBTSxLQUFLLEdBQUcsRUFBRSxDQUFDO2dCQUNuQixPQUFPLElBQUksQ0FBQyxnQkFBZ0IsQ0FBQyxJQUFJLENBQUMsQ0FBQztZQUNyQyxDQUFDO1lBRUQsNEJBQTRCO1lBQzVCLE1BQU0sU0FBUyxHQUFHLElBQUksQ0FBQyxrQkFBa0IsQ0FBQyxNQUFNLENBQUMsQ0FBQztZQUNsRCxJQUFJLFNBQVMsRUFBRSxDQUFDO2dCQUNkLE9BQU8sU0FBUyxDQUFDLEdBQUcsSUFBSSxDQUFDLENBQUM7WUFDNUIsQ0FBQztZQUVELE1BQU0sSUFBSSxxQkFBcUIsQ0FBQyxxQkFBcUIsTUFBTSxFQUFFLENBQUMsQ0FBQztRQUNqRSxDQUFDO1FBRUQsZ0RBQWdEO1FBQ2hELElBQUksSUFBSSxDQUFDLE1BQU0sQ0FBQyxJQUFJLEtBQUssa0JBQWtCLEVBQUUsQ0FBQztZQUM1QyxNQUFNLE1BQU0sR0FBRyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxNQUFNLENBQUMsTUFBTSxDQUFDLENBQUM7WUFDckQsTUFBTSxVQUFVLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxRQUFRLENBQUMsSUFBSSxDQUFDO1lBRTdDLE9BQU8sSUFBSSxDQUFDLFVBQVUsQ0FBQyxNQUFNLEVBQUUsVUFBVSxFQUFFLElBQUksQ0FBQyxDQUFDO1FBQ25ELENBQUM7UUFFRCxNQUFNLElBQUkscUJBQXFCLENBQUMsdUJBQXVCLENBQUMsQ0FBQztJQUMzRCxDQUFDO0lBRUQ7O09BRUc7SUFDSyx3QkFBd0IsQ0FBQyxJQUFTO1FBQ3hDLHlEQUF5RDtRQUN6RCxJQUFJLElBQUksQ0FBQyxRQUFRLEtBQUssSUFBSSxJQUFJLElBQUksQ0FBQyxRQUFRLEtBQUssSUFBSSxFQUFFLENBQUM7WUFDckQsTUFBTSxJQUFJLEdBQUcsQ0FBQyxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxDQUFDO1lBRTVDLElBQUksSUFBSSxDQUFDLFFBQVEsS0FBSyxJQUFJLEVBQUUsQ0FBQztnQkFDM0IsT0FBTyxJQUFJLElBQUksQ0FBQyxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDO1lBQ2pELENBQUM7aUJBQU0sQ0FBQztnQkFDTixPQUFPLElBQUksSUFBSSxDQUFDLENBQUMsSUFBSSxDQUFDLFlBQVksQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDakQsQ0FBQztRQUNILENBQUM7UUFFRCxNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsQ0FBQztRQUMxQyxNQUFNLEtBQUssR0FBRyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxLQUFLLENBQUMsQ0FBQztRQUU1QyxRQUFRLElBQUksQ0FBQyxRQUFRLEVBQUUsQ0FBQztZQUN0Qix1QkFBdUI7WUFDdkIsS0FBSyxHQUFHO2dCQUNOLElBQUksT0FBTyxJQUFJLEtBQUssUUFBUSxJQUFJLE9BQU8sS0FBSyxLQUFLLFFBQVEsRUFBRSxDQUFDO29CQUMxRCxNQUFNLElBQUksdUJBQXVCLENBQy9CLHFGQUFxRixDQUN0RixDQUFDO2dCQUNKLENBQUM7Z0JBQ0QsT0FBTyxJQUFJLEdBQUcsS0FBSyxDQUFDO1lBQ3RCLEtBQUssR0FBRztnQkFDTixJQUFJLE9BQU8sSUFBSSxLQUFLLFFBQVEsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLEVBQUUsQ0FBQztvQkFDMUQsTUFBTSxJQUFJLHVCQUF1QixDQUMvQixrREFBa0QsQ0FDbkQsQ0FBQztnQkFDSixDQUFDO2dCQUNELE9BQU8sSUFBSSxHQUFHLEtBQUssQ0FBQztZQUN0QixLQUFLLEdBQUc7Z0JBQ04sSUFBSSxPQUFPLElBQUksS0FBSyxRQUFRLElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxFQUFFLENBQUM7b0JBQzFELE1BQU0sSUFBSSx1QkFBdUIsQ0FDL0IscURBQXFELENBQ3RELENBQUM7Z0JBQ0osQ0FBQztnQkFDRCxPQUFPLElBQUksR0FBRyxLQUFLLENBQUM7WUFDdEIsS0FBSyxHQUFHLENBQUMsQ0FBQyxDQUFDO2dCQUNULElBQUksT0FBTyxJQUFJLEtBQUssUUFBUSxJQUFJLE9BQU8sS0FBSyxLQUFLLFFBQVEsRUFBRSxDQUFDO29CQUMxRCxNQUFNLElBQUksdUJBQXVCLENBQy9CLCtDQUErQyxDQUNoRCxDQUFDO2dCQUNKLENBQUM7Z0JBQ0QsZ0NBQWdDO2dCQUNoQyxJQUFJLEtBQUssS0FBSyxDQUFDLElBQUksTUFBTSxDQUFDLEVBQUUsQ0FBQyxLQUFLLEVBQUUsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDO29CQUN4QyxNQUFNLElBQUksdUJBQXVCLENBQUMsa0JBQWtCLENBQUMsQ0FBQztnQkFDeEQsQ0FBQztnQkFDRCxNQUFNLE1BQU0sR0FBRyxJQUFJLEdBQUcsS0FBSyxDQUFDO2dCQUM1QixnQ0FBZ0M7Z0JBQ2hDLElBQUksQ0FBQyxRQUFRLENBQUMsTUFBTSxDQUFDLEVBQUUsQ0FBQztvQkFDdEIsTUFBTSxJQUFJLHVCQUF1QixDQUMvQix5Q0FBeUMsQ0FDMUMsQ0FBQztnQkFDSixDQUFDO2dCQUNELE9BQU8sTUFBTSxDQUFDO1lBQ2hCLENBQUM7WUFDRCxLQUFLLEdBQUc7Z0JBQ04sSUFBSSxPQUFPLElBQUksS0FBSyxRQUFRLElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxFQUFFLENBQUM7b0JBQzFELE1BQU0sSUFBSSx1QkFBdUIsQ0FDL0IsNkNBQTZDLENBQzlDLENBQUM7Z0JBQ0osQ0FBQztnQkFDRCxJQUFJLEtBQUssS0FBSyxDQUFDLElBQUksTUFBTSxDQUFDLEVBQUUsQ0FBQyxLQUFLLEVBQUUsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDO29CQUN4QyxNQUFNLElBQUksdUJBQXVCLENBQUMsZ0JBQWdCLENBQUMsQ0FBQztnQkFDdEQsQ0FBQztnQkFDRCxxRUFBcUU7Z0JBQ3JFLDZDQUE2QztnQkFDN0MsT0FBTyxJQUFJLEdBQUcsS0FBSyxDQUFDO1lBRXRCLHVCQUF1QjtZQUN2QixLQUFLLElBQUksQ0FBQztZQUNWLEtBQUssS0FBSztnQkFDUixPQUFPLElBQUksS0FBSyxLQUFLLENBQUM7WUFDeEIsS0FBSyxJQUFJLENBQUM7WUFDVixLQUFLLEtBQUs7Z0JBQ1IsT0FBTyxJQUFJLEtBQUssS0FBSyxDQUFDO1lBQ3hCLEtBQUssR0FBRztnQkFDTixJQUFJLE9BQU8sSUFBSSxLQUFLLFFBQVEsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLEVBQUUsQ0FBQztvQkFDMUQsTUFBTSxJQUFJLHVCQUF1QixDQUMvQiwwREFBMEQsQ0FDM0QsQ0FBQztnQkFDSixDQUFDO2dCQUNELE9BQU8sSUFBSSxHQUFHLEtBQUssQ0FBQztZQUN0QixLQUFLLEdBQUc7Z0JBQ04sSUFBSSxPQUFPLElBQUksS0FBSyxRQUFRLElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxFQUFFLENBQUM7b0JBQzFELE1BQU0sSUFBSSx1QkFBdUIsQ0FDL0IsMERBQTBELENBQzNELENBQUM7Z0JBQ0osQ0FBQztnQkFDRCxPQUFPLElBQUksR0FBRyxLQUFLLENBQUM7WUFDdEIsS0FBSyxJQUFJO2dCQUNQLElBQUksT0FBTyxJQUFJLEtBQUssUUFBUSxJQUFJLE9BQU8sS0FBSyxLQUFLLFFBQVEsRUFBRSxDQUFDO29CQUMxRCxNQUFNLElBQUksdUJBQXVCLENBQy9CLDBEQUEwRCxDQUMzRCxDQUFDO2dCQUNKLENBQUM7Z0JBQ0QsT0FBTyxJQUFJLElBQUksS0FBSyxDQUFDO1lBQ3ZCLEtBQUssSUFBSTtnQkFDUCxJQUFJLE9BQU8sSUFBSSxLQUFLLFFBQVEsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLEVBQUUsQ0FBQztvQkFDMUQsTUFBTSxJQUFJLHVCQUF1QixDQUMvQiwwREFBMEQsQ0FDM0QsQ0FBQztnQkFDSixDQUFDO2dCQUNELE9BQU8sSUFBSSxJQUFJLEtBQUssQ0FBQztZQUV2QjtnQkFDRSxNQUFNLElBQUkscUJBQXFCLENBQzdCLHlCQUF5QixJQUFJLENBQUMsUUFBUSxFQUFFLENBQ3pDLENBQUM7UUFDTixDQUFDO0lBQ0gsQ0FBQztJQUVEOztPQUVHO0lBQ0ssdUJBQXVCLENBQUMsSUFBUztRQUN2QyxNQUFNLFFBQVEsR0FBRyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxRQUFRLENBQUMsQ0FBQztRQUVsRCxRQUFRLElBQUksQ0FBQyxRQUFRLEVBQUUsQ0FBQztZQUN0QixLQUFLLEdBQUc7Z0JBQ04sSUFBSSxPQUFPLFFBQVEsS0FBSyxRQUFRLElBQUksS0FBSyxDQUFDLFFBQVEsQ0FBQyxFQUFFLENBQUM7b0JBQ3BELE1BQU0sSUFBSSxxQkFBcUIsQ0FDN0IsbURBQW1ELE9BQU8sUUFBUSxFQUFFLENBQ3JFLENBQUM7Z0JBQ0osQ0FBQztnQkFDRCxPQUFPLENBQUUsUUFBbUIsQ0FBQztZQUMvQixLQUFLLEdBQUc7Z0JBQ04sSUFBSSxPQUFPLFFBQVEsS0FBSyxRQUFRLElBQUksS0FBSyxDQUFDLFFBQVEsQ0FBQyxFQUFFLENBQUM7b0JBQ3BELE1BQU0sSUFBSSxxQkFBcUIsQ0FDN0IsbURBQW1ELE9BQU8sUUFBUSxFQUFFLENBQ3JFLENBQUM7Z0JBQ0osQ0FBQztnQkFDRCxPQUFPLENBQUUsUUFBbUIsQ0FBQztZQUMvQixLQUFLLEdBQUc7Z0JBQ04sT0FBTyxDQUFDLFFBQVEsQ0FBQztZQUNuQjtnQkFDRSxNQUFNLElBQUkscUJBQXFCLENBQzdCLCtCQUErQixJQUFJLENBQUMsUUFBUSxFQUFFLENBQy9DLENBQUM7UUFDTixDQUFDO0lBQ0gsQ0FBQztJQUVEOztPQUVHO0lBQ0ssNkJBQTZCLENBQUMsSUFBUztRQUM3QyxNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsQ0FBQztRQUMxQyxPQUFPLElBQUk7WUFDVCxDQUFDLENBQUMsSUFBSSxDQUFDLFlBQVksQ0FBQyxJQUFJLENBQUMsVUFBVSxDQUFDO1lBQ3BDLENBQUMsQ0FBQyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxTQUFTLENBQUMsQ0FBQztJQUN4QyxDQUFDO0lBRUQ7O09BRUc7SUFDSyx1QkFBdUIsQ0FBQyxJQUFTO1FBQ3ZDLE1BQU0sUUFBUSxHQUFHLElBQUksQ0FBQyxRQUFRLENBQUMsR0FBRyxDQUFDLENBQUMsT0FBWSxFQUFFLEVBQUUsQ0FDbEQsSUFBSSxDQUFDLFlBQVksQ0FBQyxPQUFPLENBQUMsQ0FDM0IsQ0FBQztRQUVGLG1CQUFtQjtRQUNuQixJQUFJLFFBQVEsQ0FBQyxNQUFNLEdBQUcsbUJBQW1CLEVBQUUsQ0FBQztZQUMxQyxNQUFNLElBQUkscUJBQXFCLENBQUMsb0NBQW9DLENBQUMsQ0FBQztRQUN4RSxDQUFDO1FBRUQsT0FBTyxRQUFRLENBQUM7SUFDbEIsQ0FBQztJQUVEOztPQUVHO0lBQ0sseUJBQXlCLENBQUMsSUFBUztRQUN6QyxNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsQ0FBQztRQUUxQyxRQUFRLElBQUksQ0FBQyxRQUFRLEVBQUUsQ0FBQztZQUN0QixLQUFLLElBQUk7Z0JBQ1AsT0FBTyxJQUFJLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUMsQ0FBQyxJQUFJLENBQUM7WUFDckQsS0FBSyxJQUFJO2dCQUNQLE9BQU8sSUFBSSxDQUFDLENBQUMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDO1lBQ3JEO2dCQUNFLE1BQU0sSUFBSSxxQkFBcUIsQ0FDN0IsaUNBQWlDLElBQUksQ0FBQyxRQUFRLEVBQUUsQ0FDakQsQ0FBQztRQUNOLENBQUM7SUFDSCxDQUFDO0lBRUQ7O09BRUc7SUFDSyx1QkFBdUIsQ0FBQyxJQUFTO1FBQ3ZDLElBQUksTUFBTSxHQUFHLEVBQUUsQ0FBQztRQUNoQixLQUFLLElBQUksQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxNQUFNLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztZQUM1QyxNQUFNLElBQUksSUFBSSxDQUFDLE1BQU0sQ0FBQyxDQUFDLENBQUMsQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDO1lBQ3RDLElBQUksQ0FBQyxHQUFHLElBQUksQ0FBQyxXQUFXLENBQUMsTUFBTSxFQUFFLENBQUM7Z0JBQ2hDLE1BQU0sSUFBSSxJQUFJLENBQUMsZUFBZSxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLFdBQVcsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7WUFDekUsQ0FBQztRQUNILENBQUM7UUFDRCxPQUFPLE1BQU0sQ0FBQztJQUNoQixDQUFDO0lBRUQ7OztPQUdHO0lBQ0ssbUJBQW1CLENBQUMsSUFBUztRQUNuQyxNQUFNLE1BQU0sR0FBRyxJQUFJLENBQUMsTUFBTSxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQU0sRUFBRSxFQUFFO1lBQ3hDLElBQUksQ0FBQyxrQkFBa0IsQ0FBQyxDQUFDLENBQUMsSUFBSSxDQUFDLENBQUM7WUFDaEMsSUFBSSxDQUFDLENBQUMsSUFBSSxDQUFDLFVBQVUsQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDO2dCQUMzQixNQUFNLElBQUkscUJBQXFCLENBQzdCLG1CQUFtQixDQUFDLENBQUMsSUFBSSx1QkFBdUIsQ0FDakQsQ0FBQztZQUNKLENBQUM7WUFDRCxPQUFPLENBQUMsQ0FBQyxJQUFJLENBQUM7UUFDaEIsQ0FBQyxDQUFDLENBQUM7UUFDSCxNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsSUFBSSxDQUFDO1FBRXZCLE9BQU87WUFDTCxDQUFDLGVBQWUsQ0FBQyxFQUFFLElBQUk7WUFDdkIsTUFBTTtZQUNOLElBQUk7U0FDTCxDQUFDO0lBQ0osQ0FBQztJQUVEOztPQUVHO0lBQ0ssZUFBZSxDQUFDLEtBQWM7UUFDcEMsT0FBTyxDQUNMLE9BQU8sS0FBSyxLQUFLLFFBQVEsSUFBSSxLQUFLLEtBQUssSUFBSSxJQUFJLGVBQWUsSUFBSSxLQUFLLENBQ3hFLENBQUM7SUFDSixDQUFDO0lBRUQ7O09BRUc7SUFDSyxxQkFBcUIsQ0FDM0IsT0FBNkIsRUFDN0IsSUFBZTtRQUVmLDRCQUE0QjtRQUM1QixNQUFNLGFBQWEscUJBQVEsSUFBSSxDQUFDLFVBQVUsQ0FBRSxDQUFDO1FBRTdDLElBQUksQ0FBQztZQUNILCtDQUErQztZQUMvQyxPQUFPLENBQUMsTUFBTSxDQUFDLE9BQU8sQ0FBQyxDQUFDLEtBQWEsRUFBRSxLQUFhLEVBQUUsRUFBRTtnQkFDdEQsSUFBSSxDQUFDLFVBQVUsQ0FBQyxLQUFLLENBQUMsR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDdkMsQ0FBQyxDQUFDLENBQUM7WUFFSCwwQ0FBMEM7WUFDMUMsT0FBTyxJQUFJLENBQUMsWUFBWSxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUMsQ0FBQztRQUN6QyxDQUFDO2dCQUFTLENBQUM7WUFDVCwrQkFBK0I7WUFDL0IsSUFBSSxDQUFDLFVBQVUsR0FBRyxhQUFhLENBQUM7UUFDbEMsQ0FBQztJQUNILENBQUM7SUFFRDs7T0FFRztJQUNLLGdCQUFnQixDQUFDLElBQWU7UUFDdEMsSUFBSSxJQUFJLENBQUMsTUFBTSxLQUFLLENBQUMsSUFBSSxPQUFPLElBQUksQ0FBQyxDQUFDLENBQUMsS0FBSyxRQUFRLEVBQUUsQ0FBQztZQUNyRCxNQUFNLElBQUksdUJBQXVCLENBQy9CLDBDQUEwQyxDQUMzQyxDQUFDO1FBQ0osQ0FBQztRQUVELE1BQU0sUUFBUSxHQUFHLElBQUksQ0FBQyxDQUFDLENBQVcsQ0FBQztRQUVuQyxxQkFBcUI7UUFDckIsSUFBSSxDQUFDLGtCQUFrQixDQUFDLFFBQVEsQ0FBQyxDQUFDO1FBRWxDLElBQUksQ0FBQyxDQUFDLFFBQVEsSUFBSSxJQUFJLENBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztZQUNoQyxNQUFNLElBQUksdUJBQXVCLENBQy9CLFNBQVMsUUFBUSx3QkFBd0IsQ0FDMUMsQ0FBQztRQUNKLENBQUM7UUFFRCxPQUFPLElBQUksQ0FBQyxPQUFPLENBQUMsUUFBUSxDQUFDLENBQUM7SUFDaEMsQ0FBQztJQUVEOztPQUVHO0lBQ0ssa0JBQWtCLENBQ3hCLElBQVk7UUFFWixNQUFNLFVBQVUsR0FBb0Q7WUFDbEUsb0JBQW9CO1lBQ3BCLEtBQUssRUFBRSxHQUFHLEVBQUUsQ0FBQyxJQUFJLENBQUMsZUFBZTtZQUNqQyxNQUFNLEVBQUUsR0FBRyxFQUFFLENBQUMsSUFBSSxDQUFDLFNBQVM7WUFFNUIsb0JBQW9CO1lBQ3BCLFFBQVEsRUFBRSxDQUFDLEtBQWMsRUFBRSxZQUFxQixFQUFFLEVBQUU7Z0JBQ2xELE9BQU8sS0FBSyxLQUFLLElBQUksSUFBSSxLQUFLLEtBQUssU0FBUyxJQUFJLEtBQUssS0FBSyxFQUFFO29CQUMxRCxDQUFDLENBQUMsWUFBWTtvQkFDZCxDQUFDLENBQUMsS0FBSyxDQUFDO1lBQ1osQ0FBQztZQUVELGdCQUFnQjtZQUNoQixPQUFPLEVBQUUsQ0FBQyxLQUFjLEVBQUUsRUFBRSxDQUFDLEtBQUssS0FBSyxJQUFJO1lBQzNDLFlBQVksRUFBRSxDQUFDLEtBQWMsRUFBRSxFQUFFLENBQUMsS0FBSyxLQUFLLFNBQVM7WUFDckQsUUFBUSxFQUFFLENBQUMsS0FBYyxFQUFFLEVBQUU7Z0JBQzNCLElBQUksS0FBSyxLQUFLLElBQUksSUFBSSxLQUFLLEtBQUssU0FBUztvQkFBRSxPQUFPLElBQUksQ0FBQztnQkFDdkQsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRO29CQUFFLE9BQU8sS0FBSyxDQUFDLE1BQU0sS0FBSyxDQUFDLENBQUM7Z0JBQ3pELElBQUksS0FBSyxDQUFDLE9BQU8sQ0FBQyxLQUFLLENBQUM7b0JBQUUsT0FBTyxLQUFLLENBQUMsTUFBTSxLQUFLLENBQUMsQ0FBQztnQkFDcEQsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLEVBQUUsQ0FBQztvQkFDOUIsSUFBSSxDQUFDO3dCQUNILE9BQU8sTUFBTSxDQUFDLElBQUksQ0FBQyxLQUFLLENBQUMsQ0FBQyxNQUFNLEtBQUssQ0FBQyxDQUFDO29CQUN6QyxDQUFDO29CQUFDLFdBQU0sQ0FBQzt3QkFDUCx5RUFBeUU7d0JBQ3pFLE9BQU8sS0FBSyxDQUFDO29CQUNmLENBQUM7Z0JBQ0gsQ0FBQztnQkFDRCxPQUFPLEtBQUssQ0FBQztZQUNmLENBQUM7WUFFRCxrQkFBa0I7WUFDbEIsT0FBTyxFQUFFLENBQUMsS0FBYyxFQUFFLEVBQUUsQ0FBQyxNQUFNLENBQUMsS0FBSyxDQUFDO1lBQzFDLE9BQU8sRUFBRSxDQUFDLEtBQWMsRUFBRSxFQUFFO2dCQUMxQixNQUFNLEdBQUcsR0FBRyxNQUFNLENBQUMsS0FBSyxDQUFDLENBQUM7Z0JBQzFCLElBQUksS0FBSyxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUM7b0JBQ2YsTUFBTSxJQUFJLHVCQUF1QixDQUFDLDBCQUEwQixDQUFDLENBQUM7Z0JBQ2hFLENBQUM7Z0JBQ0QsT0FBTyxHQUFHLENBQUM7WUFDYixDQUFDO1lBQ0QsUUFBUSxFQUFFLENBQUMsS0FBYyxFQUFFLEVBQUUsQ0FBQyxPQUFPLENBQUMsS0FBSyxDQUFDO1lBRTVDLGtCQUFrQjtZQUNsQixPQUFPLEVBQUUsQ0FBQyxLQUFjLEVBQUUsRUFBRTtnQkFDMUIsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLElBQUksS0FBSyxDQUFDLE9BQU8sQ0FBQyxLQUFLLENBQUMsRUFBRSxDQUFDO29CQUN0RCxPQUFPLEtBQUssQ0FBQyxNQUFNLENBQUM7Z0JBQ3RCLENBQUM7Z0JBQ0QsTUFBTSxJQUFJLHVCQUF1QixDQUFDLGtDQUFrQyxDQUFDLENBQUM7WUFDeEUsQ0FBQztZQUNELE1BQU0sRUFBRSxDQUFDLEdBQVksRUFBRSxFQUFFO2dCQUN2QixJQUFJLENBQUMsS0FBSyxDQUFDLE9BQU8sQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDO29CQUN4QixNQUFNLElBQUksdUJBQXVCLENBQUMsMEJBQTBCLENBQUMsQ0FBQztnQkFDaEUsQ0FBQztnQkFDRCxPQUFPLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQztZQUNoQixDQUFDO1lBQ0QsS0FBSyxFQUFFLENBQUMsR0FBWSxFQUFFLEVBQUU7Z0JBQ3RCLElBQUksQ0FBQyxLQUFLLENBQUMsT0FBTyxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUM7b0JBQ3hCLE1BQU0sSUFBSSx1QkFBdUIsQ0FBQyx5QkFBeUIsQ0FBQyxDQUFDO2dCQUMvRCxDQUFDO2dCQUNELE9BQU8sR0FBRyxDQUFDLEdBQUcsQ0FBQyxNQUFNLEdBQUcsQ0FBQyxDQUFDLENBQUM7WUFDN0IsQ0FBQztZQUVELGlCQUFpQjtZQUNqQixJQUFJLEVBQUUsQ0FBQyxLQUFjLEVBQUUsRUFBRTtnQkFDdkIsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLElBQUksS0FBSyxDQUFDLEtBQUssQ0FBQyxFQUFFLENBQUM7b0JBQzlDLE1BQU0sSUFBSSx1QkFBdUIsQ0FBQyw4QkFBOEIsQ0FBQyxDQUFDO2dCQUNwRSxDQUFDO2dCQUNELE9BQU8sSUFBSSxDQUFDLEdBQUcsQ0FBQyxLQUFLLENBQUMsQ0FBQztZQUN6QixDQUFDO1lBQ0QsS0FBSyxFQUFFLENBQUMsS0FBYyxFQUFFLEVBQUU7Z0JBQ3hCLElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxJQUFJLEtBQUssQ0FBQyxLQUFLLENBQUMsRUFBRSxDQUFDO29CQUM5QyxNQUFNLElBQUksdUJBQXVCLENBQUMsK0JBQStCLENBQUMsQ0FBQztnQkFDckUsQ0FBQztnQkFDRCxPQUFPLElBQUksQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDMUIsQ0FBQztZQUNELE1BQU0sRUFBRSxDQUFDLEtBQWMsRUFBRSxFQUFFO2dCQUN6QixJQUFJLE9BQU8sS0FBSyxLQUFLLFFBQVEsSUFBSSxLQUFLLENBQUMsS0FBSyxDQUFDLEVBQUUsQ0FBQztvQkFDOUMsTUFBTSxJQUFJLHVCQUF1QixDQUFDLGdDQUFnQyxDQUFDLENBQUM7Z0JBQ3RFLENBQUM7Z0JBQ0QsT0FBTyxJQUFJLENBQUMsS0FBSyxDQUFDLEtBQUssQ0FBQyxDQUFDO1lBQzNCLENBQUM7WUFDRCxNQUFNLEVBQUUsQ0FBQyxLQUFjLEVBQUUsRUFBRTtnQkFDekIsSUFBSSxPQUFPLEtBQUssS0FBSyxRQUFRLElBQUksS0FBSyxDQUFDLEtBQUssQ0FBQyxFQUFFLENBQUM7b0JBQzlDLE1BQU0sSUFBSSx1QkFBdUIsQ0FBQyxnQ0FBZ0MsQ0FBQyxDQUFDO2dCQUN0RSxDQUFDO2dCQUNELE9BQU8sSUFBSSxDQUFDLEtBQUssQ0FBQyxLQUFLLENBQUMsQ0FBQztZQUMzQixDQUFDO1lBQ0QsSUFBSSxFQUFFLENBQUMsR0FBRyxNQUFpQixFQUFFLEVBQUU7Z0JBQzdCLElBQUksTUFBTSxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztvQkFDeEIsTUFBTSxJQUFJLHVCQUF1QixDQUMvQixxQ0FBcUMsQ0FDdEMsQ0FBQztnQkFDSixDQUFDO2dCQUNELE1BQU0sT0FBTyxHQUFHLE1BQU0sQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxFQUFFLEVBQUU7b0JBQ2xDLElBQUksT0FBTyxDQUFDLEtBQUssUUFBUSxJQUFJLEtBQUssQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDO3dCQUN0QyxNQUFNLElBQUksdUJBQXVCLENBQy9CLGlCQUFpQixDQUFDLEdBQUcsQ0FBQyx5QkFBeUIsQ0FDaEQsQ0FBQztvQkFDSixDQUFDO29CQUNELE9BQU8sQ0FBQyxDQUFDO2dCQUNYLENBQUMsQ0FBQyxDQUFDO2dCQUNILE9BQU8sSUFBSSxDQUFDLEdBQUcsQ0FBQyxHQUFHLE9BQU8sQ0FBQyxDQUFDO1lBQzlCLENBQUM7WUFDRCxJQUFJLEVBQUUsQ0FBQyxHQUFHLE1BQWlCLEVBQUUsRUFBRTtnQkFDN0IsSUFBSSxNQUFNLENBQUMsTUFBTSxLQUFLLENBQUMsRUFBRSxDQUFDO29CQUN4QixNQUFNLElBQUksdUJBQXVCLENBQy9CLHFDQUFxQyxDQUN0QyxDQUFDO2dCQUNKLENBQUM7Z0JBQ0QsTUFBTSxPQUFPLEdBQUcsTUFBTSxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLEVBQUUsRUFBRTtvQkFDbEMsSUFBSSxPQUFPLENBQUMsS0FBSyxRQUFRLElBQUksS0FBSyxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUM7d0JBQ3RDLE1BQU0sSUFBSSx1QkFBdUIsQ0FDL0IsaUJBQWlCLENBQUMsR0FBRyxDQUFDLHlCQUF5QixDQUNoRCxDQUFDO29CQUNKLENBQUM7b0JBQ0QsT0FBTyxDQUFDLENBQUM7Z0JBQ1gsQ0FBQyxDQUFDLENBQUM7Z0JBQ0gsT0FBTyxJQUFJLENBQUMsR0FBRyxDQUFDLEdBQUcsT0FBTyxDQUFDLENBQUM7WUFDOUIsQ0FBQztTQUNGLENBQUM7UUFFRixPQUFPLFVBQVUsQ0FBQyxJQUFJLENBQUMsSUFBSSxJQUFJLENBQUM7SUFDbEMsQ0FBQztJQUVEOztPQUVHO0lBQ0ssVUFBVSxDQUNoQixNQUFlLEVBQ2YsVUFBa0IsRUFDbEIsSUFBZTtRQUVmLElBQUksTUFBTSxLQUFLLElBQUksSUFBSSxNQUFNLEtBQUssU0FBUyxFQUFFLENBQUM7WUFDNUMsTUFBTSxJQUFJLHVCQUF1QixDQUMvQix5Q0FBeUMsQ0FDMUMsQ0FBQztRQUNKLENBQUM7UUFFRCx1QkFBdUI7UUFDdkIsSUFBSSxDQUFDLGtCQUFrQixDQUFDLFVBQVUsQ0FBQyxDQUFDO1FBRXBDLCtDQUErQztRQUMvQyxJQUFJLENBQUMsSUFBSSxDQUFDLGVBQWUsQ0FBQyxNQUFNLEVBQUUsVUFBVSxDQUFDLEVBQUUsQ0FBQztZQUM5QyxNQUFNLElBQUkscUJBQXFCLENBQUMsV0FBVyxVQUFVLGtCQUFrQixDQUFDLENBQUM7UUFDM0UsQ0FBQztRQUVELDhEQUE4RDtRQUM5RCxJQUFJLEtBQUssQ0FBQyxPQUFPLENBQUMsTUFBTSxDQUFDLElBQUksSUFBSSxDQUFDLGdCQUFnQixDQUFDLFVBQVUsQ0FBQyxFQUFFLENBQUM7WUFDL0QsTUFBTSxRQUFRLEdBQUcsSUFBSSxDQUFDLENBQUMsQ0FBQyxDQUFDO1lBQ3pCLElBQUksSUFBSSxDQUFDLGVBQWUsQ0FBQyxRQUFRLENBQUMsRUFBRSxDQUFDO2dCQUNuQyxPQUFPLElBQUksQ0FBQyxvQ0FBb0MsQ0FDOUMsTUFBTSxFQUNOLFVBQVUsRUFDVixRQUFRLEVBQ1IsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUMsQ0FDZCxDQUFDO1lBQ0osQ0FBQztRQUNILENBQUM7UUFFRCxpRUFBaUU7UUFDakUsSUFBSSxJQUFJLENBQUMsZ0JBQWdCLENBQUMsVUFBVSxDQUFDLEVBQUUsQ0FBQztZQUN0QyxJQUFJLENBQUMseUJBQXlCLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDdkMsQ0FBQztRQUVELDhDQUE4QztRQUM5QyxJQUFJLElBQUksQ0FBQyxpQkFBaUIsQ0FBQyxVQUFVLENBQUMsRUFBRSxDQUFDO1lBQ3ZDLElBQUksQ0FBQyxjQUFjLEVBQUUsQ0FBQztZQUN0QixJQUFJLElBQUksQ0FBQyxjQUFjLEdBQUcsY0FBYyxFQUFFLENBQUM7Z0JBQ3pDLE1BQU0sSUFBSSxxQkFBcUIsQ0FBQyxrQ0FBa0MsQ0FBQyxDQUFDO1lBQ3RFLENBQUM7UUFDSCxDQUFDO1FBRUQsaUJBQWlCO1FBQ2pCLE1BQU0sTUFBTSxHQUFJLE1BQWMsQ0FBQyxVQUFVLENBQUMsQ0FBQztRQUMzQyxJQUFJLE9BQU8sTUFBTSxLQUFLLFVBQVUsRUFBRSxDQUFDO1lBQ2pDLE1BQU0sSUFBSSx1QkFBdUIsQ0FBQyxJQUFJLFVBQVUscUJBQXFCLENBQUMsQ0FBQztRQUN6RSxDQUFDO1FBRUQseUJBQXlCO1FBQ3pCLElBQUksQ0FBQztZQUNILE1BQU0sTUFBTSxHQUFHLE1BQU0sQ0FBQyxLQUFLLENBQUMsTUFBTSxFQUFFLElBQUksQ0FBQyxDQUFDO1lBRTFDLHVCQUF1QjtZQUN2QixJQUFJLEtBQUssQ0FBQyxPQUFPLENBQUMsTUFBTSxDQUFDLElBQUksTUFBTSxDQUFDLE1BQU0sR0FBRyxtQkFBbUIsRUFBRSxDQUFDO2dCQUNqRSxNQUFNLElBQUkscUJBQXFCLENBQzdCLDJDQUEyQyxDQUM1QyxDQUFDO1lBQ0osQ0FBQztZQUNELElBQUksT0FBTyxNQUFNLEtBQUssUUFBUSxJQUFJLE1BQU0sQ0FBQyxNQUFNLEdBQUcsbUJBQW1CLEVBQUUsQ0FBQztnQkFDdEUsTUFBTSxJQUFJLHFCQUFxQixDQUM3Qiw4Q0FBOEMsQ0FDL0MsQ0FBQztZQUNKLENBQUM7WUFFRCxPQUFPLE1BQU0sQ0FBQztRQUNoQixDQUFDO1FBQUMsT0FBTyxLQUFLLEVBQUUsQ0FBQztZQUNmLElBQUksS0FBSyxZQUFZLHFCQUFxQixFQUFFLENBQUM7Z0JBQzNDLE1BQU0sS0FBSyxDQUFDO1lBQ2QsQ0FBQztZQUNELE1BQU0sSUFBSSx1QkFBdUIsQ0FDL0IsV0FBVyxVQUFVLGFBQWEsS0FBSyxDQUFDLE9BQU8sRUFBRSxDQUNsRCxDQUFDO1FBQ0osQ0FBQztJQUNILENBQUM7SUFFRDs7O09BR0c7SUFDSyxvQ0FBb0MsQ0FDMUMsS0FBZ0IsRUFDaEIsVUFBa0IsRUFDbEIsT0FBNkIsRUFDN0IsU0FBb0I7UUFFcEIsd0JBQXdCO1FBQ3hCLElBQUksQ0FBQyxjQUFjLEVBQUUsQ0FBQztRQUN0QixJQUFJLElBQUksQ0FBQyxjQUFjLEdBQUcsY0FBYyxFQUFFLENBQUM7WUFDekMsTUFBTSxJQUFJLHFCQUFxQixDQUFDLGtDQUFrQyxDQUFDLENBQUM7UUFDdEUsQ0FBQztRQUVELHNCQUFzQjtRQUN0QixJQUFJLEtBQUssQ0FBQyxNQUFNLEdBQUcsbUJBQW1CLEVBQUUsQ0FBQztZQUN2QyxNQUFNLElBQUkscUJBQXFCLENBQUMsb0NBQW9DLENBQUMsQ0FBQztRQUN4RSxDQUFDO1FBRUQsUUFBUSxVQUFVLEVBQUUsQ0FBQztZQUNuQixLQUFLLEtBQUssQ0FBQyxDQUFDLENBQUM7Z0JBQ1gsTUFBTSxNQUFNLEdBQWMsRUFBRSxDQUFDO2dCQUM3QixLQUFLLElBQUksQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLEdBQUcsS0FBSyxDQUFDLE1BQU0sRUFBRSxDQUFDLEVBQUUsRUFBRSxDQUFDO29CQUN0QyxNQUFNLEtBQUssR0FBRyxJQUFJLENBQUMscUJBQXFCLENBQUMsT0FBTyxFQUFFO3dCQUNoRCxLQUFLLENBQUMsQ0FBQyxDQUFDO3dCQUNSLENBQUM7d0JBQ0QsS0FBSztxQkFDTixDQUFDLENBQUM7b0JBQ0gsTUFBTSxDQUFDLElBQUksQ0FBQyxLQUFLLENBQUMsQ0FBQztnQkFDckIsQ0FBQztnQkFDRCxPQUFPLE1BQU0sQ0FBQztZQUNoQixDQUFDO1lBRUQsS0FBSyxRQUFRLENBQUMsQ0FBQyxDQUFDO2dCQUNkLE1BQU0sTUFBTSxHQUFjLEVBQUUsQ0FBQztnQkFDN0IsS0FBSyxJQUFJLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxHQUFHLEtBQUssQ0FBQyxNQUFNLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztvQkFDdEMsTUFBTSxhQUFhLEdBQUcsSUFBSSxDQUFDLHFCQUFxQixDQUFDLE9BQU8sRUFBRTt3QkFDeEQsS0FBSyxDQUFDLENBQUMsQ0FBQzt3QkFDUixDQUFDO3dCQUNELEtBQUs7cUJBQ04sQ0FBQyxDQUFDO29CQUNILElBQUksYUFBYSxFQUFFLENBQUM7d0JBQ2xCLE1BQU0sQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7b0JBQ3hCLENBQUM7Z0JBQ0gsQ0FBQztnQkFDRCxPQUFPLE1BQU0sQ0FBQztZQUNoQixDQUFDO1lBRUQsS0FBSyxNQUFNLENBQUMsQ0FBQyxDQUFDO2dCQUNaLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxLQUFLLENBQUMsTUFBTSxFQUFFLENBQUMsRUFBRSxFQUFFLENBQUM7b0JBQ3RDLE1BQU0sT0FBTyxHQUFHLElBQUksQ0FBQyxxQkFBcUIsQ0FBQyxPQUFPLEVBQUU7d0JBQ2xELEtBQUssQ0FBQyxDQUFDLENBQUM7d0JBQ1IsQ0FBQzt3QkFDRCxLQUFLO3FCQUNOLENBQUMsQ0FBQztvQkFDSCxJQUFJLE9BQU8sRUFBRSxDQUFDO3dCQUNaLE9BQU8sS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDO29CQUNsQixDQUFDO2dCQUNILENBQUM7Z0JBQ0QsT0FBTyxTQUFTLENBQUM7WUFDbkIsQ0FBQztZQUVELEtBQUssV0FBVyxDQUFDLENBQUMsQ0FBQztnQkFDakIsS0FBSyxJQUFJLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxHQUFHLEtBQUssQ0FBQyxNQUFNLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztvQkFDdEMsTUFBTSxPQUFPLEdBQUcsSUFBSSxDQUFDLHFCQUFxQixDQUFDLE9BQU8sRUFBRTt3QkFDbEQsS0FBSyxDQUFDLENBQUMsQ0FBQzt3QkFDUixDQUFDO3dCQUNELEtBQUs7cUJBQ04sQ0FBQyxDQUFDO29CQUNILElBQUksT0FBTyxFQUFFLENBQUM7d0JBQ1osT0FBTyxDQUFDLENBQUM7b0JBQ1gsQ0FBQztnQkFDSCxDQUFDO2dCQUNELE9BQU8sQ0FBQyxDQUFDLENBQUM7WUFDWixDQUFDO1lBRUQsS0FBSyxlQUFlLENBQUMsQ0FBQyxDQUFDO2dCQUNyQixLQUFLLElBQUksQ0FBQyxHQUFHLEtBQUssQ0FBQyxNQUFNLEdBQUcsQ0FBQyxFQUFFLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztvQkFDM0MsTUFBTSxPQUFPLEdBQUcsSUFBSSxDQUFDLHFCQUFxQixDQUFDLE9BQU8sRUFBRTt3QkFDbEQsS0FBSyxDQUFDLENBQUMsQ0FBQzt3QkFDUixDQUFDO3dCQUNELEtBQUs7cUJBQ04sQ0FBQyxDQUFDO29CQUNILElBQUksT0FBTyxFQUFFLENBQUM7d0JBQ1osT0FBTyxDQUFDLENBQUM7b0JBQ1gsQ0FBQztnQkFDSCxDQUFDO2dCQUNELE9BQU8sQ0FBQyxDQUFDLENBQUM7WUFDWixDQUFDO1lBRUQsS0FBSyxNQUFNLENBQUMsQ0FBQyxDQUFDO2dCQUNaLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxLQUFLLENBQUMsTUFBTSxFQUFFLENBQUMsRUFBRSxFQUFFLENBQUM7b0JBQ3RDLE1BQU0sT0FBTyxHQUFHLElBQUksQ0FBQyxxQkFBcUIsQ0FBQyxPQUFPLEVBQUU7d0JBQ2xELEtBQUssQ0FBQyxDQUFDLENBQUM7d0JBQ1IsQ0FBQzt3QkFDRCxLQUFLO3FCQUNOLENBQUMsQ0FBQztvQkFDSCxJQUFJLE9BQU8sRUFBRSxDQUFDO3dCQUNaLE9BQU8sSUFBSSxDQUFDO29CQUNkLENBQUM7Z0JBQ0gsQ0FBQztnQkFDRCxPQUFPLEtBQUssQ0FBQztZQUNmLENBQUM7WUFFRCxLQUFLLE9BQU8sQ0FBQyxDQUFDLENBQUM7Z0JBQ2IsS0FBSyxJQUFJLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxHQUFHLEtBQUssQ0FBQyxNQUFNLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztvQkFDdEMsTUFBTSxPQUFPLEdBQUcsSUFBSSxDQUFDLHFCQUFxQixDQUFDLE9BQU8sRUFBRTt3QkFDbEQsS0FBSyxDQUFDLENBQUMsQ0FBQzt3QkFDUixDQUFDO3dCQUNELEtBQUs7cUJBQ04sQ0FBQyxDQUFDO29CQUNILElBQUksQ0FBQyxPQUFPLEVBQUUsQ0FBQzt3QkFDYixPQUFPLEtBQUssQ0FBQztvQkFDZixDQUFDO2dCQUNILENBQUM7Z0JBQ0QsT0FBTyxJQUFJLENBQUM7WUFDZCxDQUFDO1lBRUQsS0FBSyxRQUFRLENBQUMsQ0FBQyxDQUFDO2dCQUNkLElBQUksS0FBSyxDQUFDLE1BQU0sS0FBSyxDQUFDLElBQUksU0FBUyxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztvQkFDakQsTUFBTSxJQUFJLHVCQUF1QixDQUMvQiw2Q0FBNkMsQ0FDOUMsQ0FBQztnQkFDSixDQUFDO2dCQUVELElBQUksV0FBb0IsQ0FBQztnQkFDekIsSUFBSSxVQUFrQixDQUFDO2dCQUV2QixJQUFJLFNBQVMsQ0FBQyxNQUFNLEdBQUcsQ0FBQyxFQUFFLENBQUM7b0JBQ3pCLFdBQVcsR0FBRyxTQUFTLENBQUMsQ0FBQyxDQUFDLENBQUM7b0JBQzNCLFVBQVUsR0FBRyxDQUFDLENBQUM7Z0JBQ2pCLENBQUM7cUJBQU0sQ0FBQztvQkFDTixXQUFXLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDO29CQUN2QixVQUFVLEdBQUcsQ0FBQyxDQUFDO2dCQUNqQixDQUFDO2dCQUVELEtBQUssSUFBSSxDQUFDLEdBQUcsVUFBVSxFQUFFLENBQUMsR0FBRyxLQUFLLENBQUMsTUFBTSxFQUFFLENBQUMsRUFBRSxFQUFFLENBQUM7b0JBQy9DLFdBQVcsR0FBRyxJQUFJLENBQUMscUJBQXFCLENBQUMsT0FBTyxFQUFFO3dCQUNoRCxXQUFXO3dCQUNYLEtBQUssQ0FBQyxDQUFDLENBQUM7d0JBQ1IsQ0FBQzt3QkFDRCxLQUFLO3FCQUNOLENBQUMsQ0FBQztnQkFDTCxDQUFDO2dCQUVELE9BQU8sV0FBVyxDQUFDO1lBQ3JCLENBQUM7WUFFRDtnQkFDRSxNQUFNLElBQUkscUJBQXFCLENBQzdCLGlCQUFpQixVQUFVLHlDQUF5QyxDQUNyRSxDQUFDO1FBQ04sQ0FBQztJQUNILENBQUM7SUFFRDs7T0FFRztJQUNLLGVBQWUsQ0FBQyxNQUFlLEVBQUUsVUFBa0I7UUFDekQsSUFBSSxPQUFPLE1BQU0sS0FBSyxRQUFRLEVBQUUsQ0FBQztZQUMvQixPQUFPLG1CQUFtQixDQUFDLEdBQUcsQ0FBQyxVQUFVLENBQUMsQ0FBQztRQUM3QyxDQUFDO1FBQ0QsSUFBSSxLQUFLLENBQUMsT0FBTyxDQUFDLE1BQU0sQ0FBQyxFQUFFLENBQUM7WUFDMUIsT0FBTyxrQkFBa0IsQ0FBQyxHQUFHLENBQUMsVUFBVSxDQUFDLENBQUM7UUFDNUMsQ0FBQztRQUNELElBQUksT0FBTyxNQUFNLEtBQUssUUFBUSxFQUFFLENBQUM7WUFDL0IsT0FBTyxtQkFBbUIsQ0FBQyxHQUFHLENBQUMsVUFBVSxDQUFDLENBQUM7UUFDN0MsQ0FBQztRQUNELElBQUksT0FBTyxNQUFNLEtBQUssUUFBUSxJQUFJLE1BQU0sS0FBSyxJQUFJLEVBQUUsQ0FBQztZQUNsRCxPQUFPLG1CQUFtQixDQUFDLEdBQUcsQ0FBQyxVQUFVLENBQUMsQ0FBQztRQUM3QyxDQUFDO1FBQ0QsT0FBTyxLQUFLLENBQUM7SUFDZixDQUFDO0lBRUQ7O09BRUc7SUFDSyxpQkFBaUIsQ0FBQyxVQUFrQjtRQUMxQyxPQUFPO1lBQ0wsS0FBSztZQUNMLFFBQVE7WUFDUixRQUFRO1lBQ1IsTUFBTTtZQUNOLFdBQVc7WUFDWCxlQUFlO1lBQ2YsTUFBTTtZQUNOLE9BQU87U0FDUixDQUFDLFFBQVEsQ0FBQyxVQUFVLENBQUMsQ0FBQztJQUN6QixDQUFDO0lBRUQ7O09BRUc7SUFDSyxnQkFBZ0IsQ0FBQyxVQUFrQjtRQUN6QyxPQUFPO1lBQ0wsS0FBSztZQUNMLFFBQVE7WUFDUixRQUFRO1lBQ1IsTUFBTTtZQUNOLFdBQVc7WUFDWCxNQUFNO1lBQ04sT0FBTztZQUNQLFNBQVM7U0FDVixDQUFDLFFBQVEsQ0FBQyxVQUFVLENBQUMsQ0FBQztJQUN6QixDQUFDO0lBRUQ7OztPQUdHO0lBQ0sseUJBQXlCLENBQUMsSUFBZTtRQUMvQyxLQUFLLE1BQU0sR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1lBQ3ZCLHNEQUFzRDtZQUN0RCxJQUFJLElBQUksQ0FBQyxlQUFlLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQztnQkFDOUIsU0FBUztZQUNYLENBQUM7WUFFRCx3REFBd0Q7WUFDeEQsSUFBSSxPQUFPLEdBQUcsS0FBSyxVQUFVLEVBQUUsQ0FBQztnQkFDOUIsTUFBTSxJQUFJLHFCQUFxQixDQUM3Qix5RkFBeUYsQ0FDMUYsQ0FBQztZQUNKLENBQUM7WUFFRCwwREFBMEQ7WUFDMUQsSUFBSSxHQUFHLEtBQUssSUFBSSxJQUFJLE9BQU8sR0FBRyxLQUFLLFFBQVEsRUFBRSxDQUFDO2dCQUM1QyxJQUFJLENBQUMsbUJBQW1CLENBQUMsR0FBRyxFQUFFLENBQUMsRUFBRSxJQUFJLE9BQU8sRUFBRSxDQUFDLENBQUM7WUFDbEQsQ0FBQztRQUNILENBQUM7SUFDSCxDQUFDO0lBRUQ7O09BRUc7SUFDSyxrQkFBa0IsQ0FDeEIsTUFBZSxFQUNmLFFBQXlCO1FBRXpCLElBQUksTUFBTSxLQUFLLElBQUksSUFBSSxNQUFNLEtBQUssU0FBUyxFQUFFLENBQUM7WUFDNUMsT0FBTyxTQUFTLENBQUM7UUFDbkIsQ0FBQztRQUVELDhCQUE4QjtRQUM5QixJQUNFLFFBQVEsS0FBSyxXQUFXO1lBQ3hCLFFBQVEsS0FBSyxhQUFhO1lBQzFCLFFBQVEsS0FBSyxXQUFXLEVBQ3hCLENBQUM7WUFDRCxNQUFNLElBQUkscUJBQXFCLENBQzdCLCtDQUErQyxDQUNoRCxDQUFDO1FBQ0osQ0FBQztRQUVELE9BQVEsTUFBYyxDQUFDLFFBQVEsQ0FBQyxDQUFDO0lBQ25DLENBQUM7SUFFRDs7T0FFRztJQUNLLGtCQUFrQixDQUFDLElBQVksRUFBRSxNQUFNLEdBQUcsSUFBSTtRQUNwRCw4Q0FBOEM7UUFDOUMsSUFBSSxNQUFNLEVBQUUsQ0FBQztZQUNYLE1BQU0sYUFBYSxHQUFHO2dCQUNwQixNQUFNO2dCQUNOLFVBQVU7Z0JBQ1YsYUFBYTtnQkFDYixXQUFXO2dCQUNYLFdBQVc7Z0JBQ1gsU0FBUztnQkFDVCxTQUFTO2dCQUNULFFBQVE7Z0JBQ1IsUUFBUTtnQkFDUixZQUFZO2dCQUNaLFFBQVE7Z0JBQ1IsVUFBVTtnQkFDVixVQUFVO2dCQUNWLGNBQWM7Z0JBQ2QsZ0JBQWdCO2FBQ2pCLENBQUM7WUFFRixJQUFJLGFBQWEsQ0FBQyxRQUFRLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQztnQkFDakMsTUFBTSxJQUFJLHFCQUFxQixDQUFDLG9CQUFvQixJQUFJLGtCQUFrQixDQUFDLENBQUM7WUFDOUUsQ0FBQztRQUNILENBQUM7YUFBTSxDQUFDO1lBQ04sMkRBQTJEO1lBQzNELE1BQU0sZ0JBQWdCLEdBQUc7Z0JBQ3ZCLFdBQVc7Z0JBQ1gsYUFBYTtnQkFDYixXQUFXO2FBQ1osQ0FBQztZQUVGLElBQUksZ0JBQWdCLENBQUMsUUFBUSxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUM7Z0JBQ3BDLE1BQU0sSUFBSSxxQkFBcUIsQ0FBQyxhQUFhLElBQUksa0JBQWtCLENBQUMsQ0FBQztZQUN2RSxDQUFDO1FBQ0gsQ0FBQztJQUNILENBQUM7SUFFRDs7T0FFRztJQUNLLHNCQUFzQixDQUFDLFFBQXlCO1FBQ3RELElBQUksT0FBTyxRQUFRLEtBQUssUUFBUSxFQUFFLENBQUM7WUFDakMsSUFBSSxDQUFDLGtCQUFrQixDQUFDLFFBQVEsRUFBRSxLQUFLLENBQUMsQ0FBQztRQUMzQyxDQUFDO0lBQ0gsQ0FBQztJQUVEOztPQUVHO0lBQ0ssa0JBQWtCLENBQUMsSUFBWTtRQUNyQyxJQUFJLENBQUMsa0JBQWtCLENBQUMsSUFBSSxFQUFFLEtBQUssQ0FBQyxDQUFDO0lBQ3ZDLENBQUM7SUFFRDs7O09BR0c7SUFDSyxtQkFBbUIsQ0FDekIsSUFBYSxFQUNiLEtBQUssR0FBRyxDQUFDLEVBQ1QsSUFBSSxHQUFHLElBQUksT0FBTyxFQUFFO1FBRXBCLGdEQUFnRDtRQUNoRCxJQUFJLEtBQUssR0FBRyxFQUFFLEVBQUUsQ0FBQztZQUNmLE1BQU0sSUFBSSxxQkFBcUIsQ0FDN0IsMkRBQTJELENBQzVELENBQUM7UUFDSixDQUFDO1FBRUQsbUJBQW1CO1FBQ25CLElBQUksT0FBTyxJQUFJLEtBQUssVUFBVSxFQUFFLENBQUM7WUFDL0IsTUFBTSxJQUFJLHFCQUFxQixDQUM3QiwyQ0FBMkMsQ0FDNUMsQ0FBQztRQUNKLENBQUM7UUFFRCxnQkFBZ0I7UUFDaEIsSUFBSSxLQUFLLENBQUMsT0FBTyxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUM7WUFDeEIsZ0NBQWdDO1lBQ2hDLElBQUksSUFBSSxDQUFDLEdBQUcsQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDO2dCQUNuQixNQUFNLElBQUkscUJBQXFCLENBQzdCLHFEQUFxRCxDQUN0RCxDQUFDO1lBQ0osQ0FBQztZQUNELElBQUksQ0FBQyxHQUFHLENBQUMsSUFBSSxDQUFDLENBQUM7WUFFZix3QkFBd0I7WUFDeEIsS0FBSyxNQUFNLElBQUksSUFBSSxJQUFJLEVBQUUsQ0FBQztnQkFDeEIsSUFBSSxDQUFDLG1CQUFtQixDQUFDLElBQUksRUFBRSxLQUFLLEdBQUcsQ0FBQyxFQUFFLElBQUksQ0FBQyxDQUFDO1lBQ2xELENBQUM7WUFDRCxPQUFPO1FBQ1QsQ0FBQztRQUVELGlCQUFpQjtRQUNqQixJQUFJLElBQUksS0FBSyxJQUFJLElBQUksT0FBTyxJQUFJLEtBQUssUUFBUSxFQUFFLENBQUM7WUFDOUMsZ0NBQWdDO1lBQ2hDLElBQUksSUFBSSxDQUFDLEdBQUcsQ0FBQyxJQUFjLENBQUMsRUFBRSxDQUFDO2dCQUM3QixNQUFNLElBQUkscUJBQXFCLENBQzdCLHFEQUFxRCxDQUN0RCxDQUFDO1lBQ0osQ0FBQztZQUNELElBQUksQ0FBQyxHQUFHLENBQUMsSUFBYyxDQUFDLENBQUM7WUFFekIseUJBQXlCO1lBQ3pCLEtBQUssTUFBTSxHQUFHLElBQUksSUFBSSxFQUFFLENBQUM7Z0JBQ3ZCLElBQUksTUFBTSxDQUFDLFNBQVMsQ0FBQyxjQUFjLENBQUMsSUFBSSxDQUFDLElBQUksRUFBRSxHQUFHLENBQUMsRUFBRSxDQUFDO29CQUNwRCx5QkFBeUI7b0JBQ3pCLElBQUksQ0FBQyxrQkFBa0IsQ0FBQyxHQUFHLEVBQUUsS0FBSyxDQUFDLENBQUM7b0JBQ3BDLDBCQUEwQjtvQkFDMUIsSUFBSSxDQUFDLG1CQUFtQixDQUFFLElBQVksQ0FBQyxHQUFHLENBQUMsRUFBRSxLQUFLLEdBQUcsQ0FBQyxFQUFFLElBQUksQ0FBQyxDQUFDO2dCQUNoRSxDQUFDO1lBQ0gsQ0FBQztRQUNILENBQUM7UUFFRCxpRUFBaUU7SUFDbkUsQ0FBQztJQUVEOztPQUVHO0lBQ0ssZUFBZSxDQUFDLEtBQWM7UUFDcEMsSUFBSSxLQUFLLEtBQUssSUFBSSxJQUFJLEtBQUssS0FBSyxTQUFTLEVBQUUsQ0FBQztZQUMxQyxPQUFPLEVBQUUsQ0FBQztRQUNaLENBQUM7UUFDRCxJQUFJLE9BQU8sS0FBSyxLQUFLLFFBQVEsRUFBRSxDQUFDO1lBQzlCLE9BQU8sS0FBSyxDQUFDO1FBQ2YsQ0FBQztRQUNELElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxJQUFJLE9BQU8sS0FBSyxLQUFLLFNBQVMsRUFBRSxDQUFDO1lBQzVELE9BQU8sTUFBTSxDQUFDLEtBQUssQ0FBQyxDQUFDO1FBQ3ZCLENBQUM7UUFDRCxJQUFJLEtBQUssQ0FBQyxPQUFPLENBQUMsS0FBSyxDQUFDLElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxFQUFFLENBQUM7WUFDdEQsSUFBSSxDQUFDO2dCQUNILE9BQU8sSUFBSSxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsQ0FBQztZQUMvQixDQUFDO1lBQUMsV0FBTSxDQUFDO2dCQUNQLE9BQU8sVUFBVSxDQUFDO1lBQ3BCLENBQUM7UUFDSCxDQUFDO1FBQ0QsT0FBTyxNQUFNLENBQUMsS0FBSyxDQUFDLENBQUM7SUFDdkIsQ0FBQztDQUNGO0FBRUQsZUFBZSx3QkFBd0IsQ0FBQyJ9